import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Button, Spin, Empty, Tooltip, Popconfirm, Typography, Modal, Tabs, Select, AutoComplete, Tag, Input, Dropdown, Alert, Table, Switch, DatePicker, message as antMessage } from 'antd';
import { LeftOutlined, RightOutlined, DeleteOutlined, ReloadOutlined, TableOutlined, BarChartOutlined, FullscreenOutlined, FullscreenExitOutlined, DownloadOutlined, SettingOutlined, FilterOutlined, ClearOutlined, PlusOutlined, EditOutlined, StarOutlined, StarFilled, ZoomInOutlined, HomeOutlined, ShareAltOutlined, CloseOutlined, FolderOpenOutlined, CopyOutlined, CodeOutlined, EyeOutlined, MoreOutlined, ExperimentOutlined, DownOutlined, UpOutlined, CaretRightOutlined, SaveOutlined, PoweroffOutlined } from '@ant-design/icons';
import { Responsive } from 'react-grid-layout';
import dayjs from 'dayjs';
import { useDashboard, DRAFT_DASHBOARD_ID, normalizeTabs } from '../contexts/DashboardContext';
import { useAuth } from '../contexts/AuthContext';
import { executeDashboardSQL, cloneDashboard, getDashboardJson, updateDashboardFromJson } from '../services/dashboardService';
import { FilterableTable, FilterableChart, parseWhereFilters, rebuildSqlWithFilters } from './DeliveryFilters';
import CustomFilterEditorModal from './CustomFilterEditorModal';
import { wrapSqlWithDashboardFilters } from '../utils/dashboardFilterWrapper';
import AdvancedTable from './AdvancedTable';
import DashboardHomepage from './DashboardHomepage';
import DashboardLibrary from './DashboardLibrary';
import DashboardShareDialog from './DashboardShareDialog';
import ChartEditorPanel from './ChartEditorPanel';
import PlotlyChartCard from './PlotlyChartCard';
import MetricCard from './MetricCard';
import Skeleton from './Skeleton';
import ErrorPanel from './ErrorPanel';
import EmptyLaunchpad from './EmptyLaunchpad';
import { setChartData as setEditorCache } from '../utils/chartDataCache';

import './dashboard-tokens.css';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

/**
 * Heuristic: does ``chart_config`` look like a Plotly figure produced
 * by the in-app Chart Editor? Plotly figures shape as
 * ``{data: [trace, ...], layout: {...}}`` with at least one trace
 * carrying a ``type`` field. Older "chart" items predate the editor
 * and use a Vega-Lite-ish ``chartSpec`` consumed by FilterableChart;
 * we route those to the legacy renderer for backward compatibility.
 */
function isPlotlyFigure(chartConfig) {
  if (!chartConfig || typeof chartConfig !== 'object') return false;
  if (chartConfig.metric) return false;
  if (!Array.isArray(chartConfig.data)) return false;
  return chartConfig.data.some((t) => t && typeof t === 'object' && (t.type || t.xsrc || t.ysrc));
}

const { Text } = Typography;

const TABLE_ROW_HEIGHT = 28;
const TABLE_OVERSCAN = 10; // extra rows above/below viewport
const GRID_GAP = 12; // Gap between grid items (px)

// Runtime-only memory shared across DashboardPanel remounts.
// Survives SPA route changes, resets on full reload.
const runtimeLastActiveTabByDashboard = {};
const runtimeOpenedDashboardOnce = {};

function rememberDashboardTab(ref, dashboardId, tabName) {
  if (dashboardId == null || dashboardId === DRAFT_DASHBOARD_ID || !tabName) return;
  const key = String(dashboardId);
  ref.current[key] = tabName;
  runtimeLastActiveTabByDashboard[key] = tabName;
}

function getRememberedDashboardTab(ref, dashboardId) {
  if (dashboardId == null) return null;
  const key = String(dashboardId);
  return ref.current[key] || runtimeLastActiveTabByDashboard[key] || null;
}

/**
 * Virtualized table — only renders rows visible in the scroll viewport.
 */
function VirtualTable({ columns, rows }) {
  const containerRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [containerHeight, setContainerHeight] = useState(300);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) setContainerHeight(entry.contentRect.height);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const handleScroll = useCallback((e) => setScrollTop(e.target.scrollTop), []);

  const totalHeight = rows.length * TABLE_ROW_HEIGHT;
  const startIndex = Math.max(0, Math.floor(scrollTop / TABLE_ROW_HEIGHT) - TABLE_OVERSCAN);
  const visibleCount = Math.ceil(containerHeight / TABLE_ROW_HEIGHT) + TABLE_OVERSCAN * 2;
  const endIndex = Math.min(rows.length, startIndex + visibleCount);
  const visibleRows = rows.slice(startIndex, endIndex);

  return (
    <div ref={containerRef} style={{ overflow: 'auto', height: '100%', fontSize: 12 }} onScroll={handleScroll}>
      <table className="dashboard-table">
        <thead>
          <tr>
            {columns.map((col, i) => (
              <th key={i}>{col}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {startIndex > 0 && (
            <tr style={{ height: startIndex * TABLE_ROW_HEIGHT }}><td colSpan={columns.length} /></tr>
          )}
          {visibleRows.map((row, ri) => (
            <tr key={startIndex + ri}>
              {row.map((cell, ci) => (
                <td key={ci}>
                  {cell === null ? <span style={{ color: '#bfbfbf', fontStyle: 'italic' }}>null</span> : String(cell)}
                </td>
              ))}
            </tr>
          ))}
          {endIndex < rows.length && (
            <tr style={{ height: (rows.length - endIndex) * TABLE_ROW_HEIGHT }}><td colSpan={columns.length} /></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Dashboard-level filter bar — aggregates columns from all components and
 * lets the user filter across the entire dashboard.
 * Common columns (present in all loaded items) are visible by default.
 */
function DashboardFilterBar() {
  const {
    dashboardFilters, setDashboardFilter, clearDashboardFilters,
    filterVisibility, setFilterVisibility, itemColumnData,
    customFilterDefs, customFilterValues,
    addCustomFilter, patchCustomFilter, removeCustomFilter,
    setCustomFilterValue, clearCustomFilterValues, viewMode, activeDashboardId, activeDashboardTab,
    dashboards, updateDashboardMetadata,
  } = useDashboard();
  // Edit-only affordances: create/edit/delete user-defined filters and
  // toggle visibility of auto-generated filters. In view mode the bar
  // is read-only (values can still be changed, but the structural
  // editing controls are hidden).
  const isEditMode = viewMode === 'edit';
  const [customEditor, setCustomEditor] = useState({ open: false, initial: null });
  const openCreateCustom = () => setCustomEditor({ open: true, initial: null });
  const openEditCustom = (def) => setCustomEditor({ open: true, initial: def });
  const closeCustomEditor = () => setCustomEditor({ open: false, initial: null });
  const submitCustom = async (def) => {
    if (customEditor.initial) await patchCustomFilter(customEditor.initial.id, def);
    else await addCustomFilter(def);
  };
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedPresetId, setSelectedPresetId] = useState(null);
  const activeDashboardSummary = useMemo(
    () => dashboards.find((d) => d.id === activeDashboardId) || null,
    [dashboards, activeDashboardId]
  );
  const tabFilterStore = useMemo(() => {
    const raw = activeDashboardSummary?.tab_settings;
    return raw && typeof raw === 'object' ? raw : {};
  }, [activeDashboardSummary]);
  const activeTabFilterDefs = useMemo(
    () => (Array.isArray(tabFilterStore?.[activeDashboardTab]?.tab_filter_definitions)
      ? tabFilterStore[activeDashboardTab].tab_filter_definitions
      : []),
    [tabFilterStore, activeDashboardTab]
  );
  const [pendingTabFilterValues, setPendingTabFilterValues] = useState({});
  const [tabFilterEditor, setTabFilterEditor] = useState({ open: false, initial: null });

  const persistTabFilterStore = useCallback(async (next) => {
    if (!activeDashboardId || activeDashboardId === DRAFT_DASHBOARD_ID) return;
    try {
      await updateDashboardMetadata(activeDashboardId, { tab_settings: next });
    } catch (err) {
      antMessage.error(err?.message || 'Failed to save tab filter settings');
    }
  }, [activeDashboardId, updateDashboardMetadata]);

  const applyFilterMapsToContext = useCallback((autoMap, customMap) => {
    clearDashboardFilters();
    Object.entries(autoMap || {}).forEach(([k, vals]) => {
      if (Array.isArray(vals) && vals.length > 0) setDashboardFilter(k, vals);
    });

    // Use clearCustomFilterValues() instead of iterating customFilterValues.
    // Previously, reading `customFilterValues` inside this callback caused it to
    // get a new reference every time customFilterValues changed — which in turn
    // re-fired the tab useEffect that depends on this callback, which called
    // setCustomFilterValue again, changing customFilterValues, ad infinitum.
    // This loop immediately overwrote any pending user selection with stale
    // tab-store values, making selected values vanish the moment they were picked.
    clearCustomFilterValues();
    Object.entries(customMap || {}).forEach(([id, vals]) => {
      if (Array.isArray(vals) && vals.length > 0) setCustomFilterValue(id, vals);
    });
  }, [clearDashboardFilters, clearCustomFilterValues, setCustomFilterValue, setDashboardFilter]);

  const setPendingTabCustom = useCallback((id, values) => {
    setPendingTabFilterValues((prev) => ({
      ...prev,
      [id]: Array.isArray(values) ? values : (values ? [values] : []),
    }));
  }, []);

  // ── SQL-sourced custom-filter option lists ─────────────────────────
  // Map<filterId, { loading, options: [{value,label}], error }>. Fetched
  // once per (filter id, sql) pair by running the user-supplied SELECT
  // through the same dashboard SQL endpoint the cards use.
  const [sqlFilterOptions, setSqlFilterOptions] = useState({});
  // Stable ref to the latest options map so the fetcher can read cache
  // without re-creating itself on every state update.
  const sqlOptsRef = useRef({});
  useEffect(() => { sqlOptsRef.current = sqlFilterOptions; }, [sqlFilterOptions]);

  const fetchSqlFilterOptions = useCallback(async (def, { force = false } = {}) => {
    if (!def || def.value_source?.type !== 'sql') return;
    const sql = def.value_source?.sql;
    if (typeof sql !== 'string' || !sql.trim()) return;
    const sig = `${def.id}::${sql}`;
    const cached = sqlOptsRef.current[def.id];
    if (!force && cached && cached.sig === sig && !cached.error && !cached.loading) return;
    setSqlFilterOptions((prev) => ({ ...prev, [def.id]: { sig, loading: true, options: cached?.options || [], error: null } }));
    try {
      // eslint-disable-next-line no-console
      console.debug('[customFilter] fetching options for', def.label, sql);
      const res = await executeDashboardSQL(sql, 1000, false);
      const payload = res?.data && typeof res.data === 'object' ? res.data : res;
      const rows = Array.isArray(payload?.rows) ? payload.rows : [];
      const cols = Array.isArray(payload?.columns) ? payload.columns : [];
      const namedIdx = cols.indexOf(def.column);
      const colIdx = namedIdx >= 0 ? namedIdx : 0;
      const seen = new Set();
      const opts = [];
      for (const row of rows) {
        let v;
        if (Array.isArray(row)) v = row[colIdx];
        else if (row && typeof row === 'object') v = row[cols[colIdx]];
        else v = row;
        if (v === null || v === undefined || v === '') continue;
        const s = String(v);
        if (seen.has(s)) continue;
        seen.add(s);
        opts.push({ value: s, label: s });
      }
      setSqlFilterOptions((prev) => ({ ...prev, [def.id]: { sig, loading: false, options: opts, error: null } }));
    } catch (err) {
      setSqlFilterOptions((prev) => ({ ...prev, [def.id]: { sig, loading: false, options: [], error: err?.message || 'Failed to load options' } }));
    }
  }, []);

  // Auto-fetch on definition change.
  useEffect(() => {
    [...(customFilterDefs || []), ...(activeTabFilterDefs || [])].forEach((def) => {
      if (def?.value_source?.type === 'sql') fetchSqlFilterOptions(def);
    });
  }, [customFilterDefs, activeTabFilterDefs, fetchSqlFilterOptions]);
  // Pending (uncommitted) filter values — committed to context only on Apply
  const [pendingFilters, setPendingFilters] = useState(dashboardFilters);
  // Pending (uncommitted) custom-filter values — same Apply-button
  // commit semantics as `pendingFilters` above. Keyed by filter def id.
  const [pendingCustomValues, setPendingCustomValues] = useState(customFilterValues || {});
  // Per-column inline warning (auto-clears after ~2.5s). Shown when the
  // user tries to clear a required (WHERE-driven) filter — a global
  // antMessage toast is too wide for the dense filter row.
  const [warnings, setWarnings] = useState({});
  const warnFor = useCallback((col, msg) => {
    setWarnings(prev => ({ ...prev, [col]: msg }));
    setTimeout(() => setWarnings(prev => {
      if (prev[col] !== msg) return prev;
      const next = { ...prev }; delete next[col]; return next;
    }), 2500);
  }, []);

  // Sync pending state when context updates from outside (e.g. clearAll, navigation)
  useEffect(() => {
    setPendingFilters(dashboardFilters);
  }, [dashboardFilters]);
  useEffect(() => {
    setPendingCustomValues(customFilterValues || {});
  }, [customFilterValues]);

  useEffect(() => {
    if (!activeDashboardTab) return;
    const entry = tabFilterStore?.[activeDashboardTab] || {};
    const auto = entry.auto_filters && typeof entry.auto_filters === 'object' ? entry.auto_filters : {};
    const custom = entry.custom_filter_values && typeof entry.custom_filter_values === 'object' ? entry.custom_filter_values : {};
    const tabCustom = entry.tab_filter_values && typeof entry.tab_filter_values === 'object' ? entry.tab_filter_values : {};
    applyFilterMapsToContext(auto, custom);
    setPendingFilters(auto);
    setPendingCustomValues(custom);
    setPendingTabFilterValues(tabCustom);
    setSelectedPresetId(null);
  }, [activeDashboardTab, tabFilterStore, applyFilterMapsToContext]);

  const allColumns = useMemo(() => {
    const s = new Set();
    Object.values(itemColumnData).forEach(({ columns }) => columns.forEach(c => s.add(c)));
    return [...s].sort();
  }, [itemColumnData]);

  const columnValues = useMemo(() => {
    const r = {};
    allColumns.forEach(col => {
      const s = new Set();
      Object.values(itemColumnData).forEach(({ uniqueValues }) => (uniqueValues[col] || []).forEach(v => s.add(v)));
      r[col] = [...s].sort((a, b) => {
        if (a === '(null)') return 1; if (b === '(null)') return -1;
        const na = Number(a), nb = Number(b);
        if (!isNaN(na) && !isNaN(nb)) return na - nb;
        return a.localeCompare(b);
      });
    });
    return r;
  }, [allColumns, itemColumnData]);

  const loadedIds = useMemo(() => Object.keys(itemColumnData), [itemColumnData]);

  const commonColumns = useMemo(() => {
    if (loadedIds.length === 0) return [];
    return allColumns.filter(col => loadedIds.every(id => (itemColumnData[id]?.columns || []).includes(col)));
  }, [allColumns, loadedIds, itemColumnData]);

  // Set of columns that any item filters via SQL WHERE — these need free-form input
  // because the result set only contains the currently filtered value(s).
  const whereFilteredColumns = useMemo(() => {
    const s = new Set();
    Object.values(itemColumnData).forEach(({ whereColumns }) => {
      if (!whereColumns) return;
      // whereColumns is { col: operatorType }; iterate keys
      Object.keys(whereColumns).forEach(c => s.add(c));
    });
    return s;
  }, [itemColumnData]);

  // Per-column operator type. If multiple items use the same column, prefer
  // 'in' (multi-value) since it's the most permissive UI. Otherwise the
  // first non-'in' type wins. Used to pick single-value vs multi-value input.
  const whereColumnTypes = useMemo(() => {
    const m = {};
    Object.values(itemColumnData).forEach(({ whereColumns }) => {
      if (!whereColumns) return;
      Object.entries(whereColumns).forEach(([col, info]) => {
        const type = typeof info === 'string' ? info : info.type;
        if (m[col] === 'in' || type === 'in') m[col] = 'in';
        else if (!m[col]) m[col] = type;
      });
    });
    return m;
  }, [itemColumnData]);

  // Per-column display label: "COL" for `=`/`IN`, "COL >=" for comparison
  // operators, etc. — mirrors what the chat WHERE chips show. Picks the
  // first non-empty label found across items for that column.
  const whereColumnLabels = useMemo(() => {
    const m = {};
    Object.values(itemColumnData).forEach(({ whereColumns }) => {
      if (!whereColumns) return;
      Object.entries(whereColumns).forEach(([col, info]) => {
        if (m[col]) return;
        if (typeof info === 'object' && info && info.displayLabel) m[col] = info.displayLabel;
      });
    });
    return m;
  }, [itemColumnData]);

  // Per-column readonly flag — IS NULL / IS NOT NULL / opaque predicates
  // are hidden from the dashboard filter bar (no editable RHS). They
  // remain visible in the per-component SQL modal.
  const whereColumnReadonly = useMemo(() => {
    const m = {};
    Object.values(itemColumnData).forEach(({ whereColumns }) => {
      if (!whereColumns) return;
      Object.entries(whereColumns).forEach(([col, info]) => {
        if (typeof info === 'object' && info && info.readonly) m[col] = true;
      });
    });
    return m;
  }, [itemColumnData]);

  // Per-column operator (=, IN, NOT IN, >=, …) — used in the collapsed
  // pill row so users see "TEAM_NAME = 404DNF" instead of just
  // "TEAM_NAME 404DNF".
  const whereColumnOperators = useMemo(() => {
    const m = {};
    Object.values(itemColumnData).forEach(({ whereColumns }) => {
      if (!whereColumns) return;
      Object.entries(whereColumns).forEach(([col, info]) => {
        if (m[col]) return;
        if (typeof info === 'object' && info && info.operator) m[col] = info.operator;
      });
    });
    return m;
  }, [itemColumnData]);

  // Per-column parsed default values (the literal(s) on the RHS of the
  // WHERE predicate). For BETWEEN this is `[lo, hi]`; for single-value
  // ops it's `[val]`. Used to pre-populate the dashboard top filter row
  // so users see and can edit the current bounds without first picking
  // from a dropdown.
  const whereColumnDefaults = useMemo(() => {
    const m = {};
    Object.values(itemColumnData).forEach(({ whereColumns }) => {
      if (!whereColumns) return;
      Object.entries(whereColumns).forEach(([col, info]) => {
        if (m[col]) return;
        if (typeof info === 'object' && info && Array.isArray(info.values)) m[col] = info.values;
      });
    });
    return m;
  }, [itemColumnData]);

  const AUTO_VISIBLE_CAP = 8;
  const autoVisibleColumns = useMemo(
    () => commonColumns.slice(0, AUTO_VISIBLE_CAP),
    [commonColumns]
  );

  const visibleColumns = useMemo(() =>
    allColumns.filter(col => {
      if (filterVisibility[col] !== undefined) return filterVisibility[col];
      return autoVisibleColumns.includes(col);
    }),
    [allColumns, filterVisibility, autoVisibleColumns]
  );

  const activeAutoCount = Object.values(dashboardFilters).filter(v => v && v.length > 0).length;
  const activeFilters = Object.entries(dashboardFilters).filter(([, v]) => v && v.length > 0);
  // Count custom dashboard-level filters that have a value that would
  // actually produce a predicate (mirrors hasActiveDashboardFilters but
  // per-definition so we can include them in the collapsed-mode badge).
  const activeCustomCount = (customFilterDefs || []).reduce((n, def) => {
    const vals = (customFilterValues && customFilterValues[def.id]) || def.default_values || [];
    if (!Array.isArray(vals)) return n;
    const op = (def?.operator || '').toUpperCase();
    if (op === 'IS NULL' || op === 'IS NOT NULL') {
      return vals.some((v) => v !== undefined && v !== '' && v !== false) ? n + 1 : n;
    }
    return vals.filter((v) => v !== undefined && v !== '').length > 0 ? n + 1 : n;
  }, 0);
  const activeTabCustomCount = (activeTabFilterDefs || []).reduce((n, def) => {
    const vals = (pendingTabFilterValues && pendingTabFilterValues[def.id]) || [];
    if (!Array.isArray(vals)) return n;
    const op = (def?.operator || '').toUpperCase();
    if (op === 'IS NULL' || op === 'IS NOT NULL') {
      return vals.some((v) => v !== undefined && v !== '' && v !== false) ? n + 1 : n;
    }
    return vals.filter((v) => v !== undefined && v !== '').length > 0 ? n + 1 : n;
  }, 0);
  const activeCount = activeAutoCount + activeCustomCount + activeTabCustomCount;

  // Has uncommitted changes vs context
  const isDirty = useMemo(() => {
    const keys = new Set([...Object.keys(pendingFilters || {}), ...Object.keys(dashboardFilters || {})]);
    for (const k of keys) {
      const a = (pendingFilters[k] || []).slice().sort();
      const b = (dashboardFilters[k] || []).slice().sort();
      if (a.length !== b.length) return true;
      for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return true;
    }
    const ckeys = new Set([...Object.keys(pendingCustomValues || {}), ...Object.keys(customFilterValues || {})]);
    for (const k of ckeys) {
      const a = (pendingCustomValues[k] || []).slice().map(String).sort();
      const b = (customFilterValues[k] || []).slice().map(String).sort();
      if (a.length !== b.length) return true;
      for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return true;
    }
    const tabStored = (tabFilterStore?.[activeDashboardTab]?.tab_filter_values && typeof tabFilterStore[activeDashboardTab].tab_filter_values === 'object')
      ? tabFilterStore[activeDashboardTab].tab_filter_values
      : {};
    const tkeys = new Set([...Object.keys(pendingTabFilterValues || {}), ...Object.keys(tabStored || {})]);
    for (const k of tkeys) {
      const a = (pendingTabFilterValues[k] || []).slice().map(String).sort();
      const b = (tabStored[k] || []).slice().map(String).sort();
      if (a.length !== b.length) return true;
      for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return true;
    }
    return false;
  }, [pendingFilters, dashboardFilters, pendingCustomValues, customFilterValues, pendingTabFilterValues, tabFilterStore, activeDashboardTab]);

  const applyPending = () => {
    // eslint-disable-next-line no-console
    console.debug('[DBG apply] pending=', pendingFilters, 'dashboard=', dashboardFilters, 'pendingCustom=', pendingCustomValues, 'custom=', customFilterValues);
    const keys = new Set([...Object.keys(pendingFilters || {}), ...Object.keys(dashboardFilters || {})]);
    keys.forEach(k => {
      const next = pendingFilters[k] || [];
      const prev = dashboardFilters[k] || [];
      const changed = next.length !== prev.length || next.some((v, i) => v !== prev[i]);
      // eslint-disable-next-line no-console
      console.debug('[DBG apply] dashboardFilter', k, 'changed=', changed, 'prev=', prev, 'next=', next);
      if (changed) setDashboardFilter(k, next);
    });
    const ckeys = new Set([...Object.keys(pendingCustomValues || {}), ...Object.keys(customFilterValues || {})]);
    ckeys.forEach(k => {
      const next = pendingCustomValues[k] || [];
      const prev = customFilterValues[k] || [];
      const changed = next.length !== prev.length || next.some((v, i) => v !== prev[i]);
      // eslint-disable-next-line no-console
      console.debug('[DBG apply] customFilter', k, 'changed=', changed, 'prev=', prev, 'next=', next);
      if (changed) setCustomFilterValue(k, next);
    });

    if (activeDashboardTab) {
      const nextStore = {
        ...tabFilterStore,
        [activeDashboardTab]: {
          ...(tabFilterStore[activeDashboardTab] || {}),
          auto_filters: pendingFilters || {},
          custom_filter_values: pendingCustomValues || {},
          tab_filter_values: pendingTabFilterValues || {},
          tab_filter_definitions: Array.isArray(tabFilterStore?.[activeDashboardTab]?.tab_filter_definitions)
            ? tabFilterStore[activeDashboardTab].tab_filter_definitions
            : [],
          presets: Array.isArray(tabFilterStore?.[activeDashboardTab]?.presets)
            ? tabFilterStore[activeDashboardTab].presets
            : [],
        },
      };
      void persistTabFilterStore(nextStore);
    }
  };

  const clearAll = () => {
    setPendingFilters({});
    setPendingCustomValues({});
    setPendingTabFilterValues({});
    clearDashboardFilters();
    (customFilterDefs || []).forEach((def) => setCustomFilterValue(def.id, []));

    if (activeDashboardTab) {
      const nextStore = {
        ...tabFilterStore,
        [activeDashboardTab]: {
          ...(tabFilterStore[activeDashboardTab] || {}),
          auto_filters: {},
          custom_filter_values: {},
          tab_filter_values: {},
          tab_filter_definitions: Array.isArray(tabFilterStore?.[activeDashboardTab]?.tab_filter_definitions)
            ? tabFilterStore[activeDashboardTab].tab_filter_definitions
            : [],
          presets: Array.isArray(tabFilterStore?.[activeDashboardTab]?.presets)
            ? tabFilterStore[activeDashboardTab].presets
            : [],
        },
      };
      void persistTabFilterStore(nextStore);
    }
  };

  const activeTabPresets = useMemo(
    () => (Array.isArray(tabFilterStore?.[activeDashboardTab]?.presets)
      ? tabFilterStore[activeDashboardTab].presets
      : []),
    [tabFilterStore, activeDashboardTab]
  );

  const saveCurrentAsPreset = useCallback(() => {
    if (!activeDashboardTab) return;
    const existing = activeTabPresets;
    const nextName = `${activeDashboardTab} Preset ${existing.length + 1}`;
    const preset = {
      id: `p_${Date.now()}`,
      name: nextName,
      auto_filters: pendingFilters || {},
      custom_filter_values: pendingCustomValues || {},
      tab_filter_values: pendingTabFilterValues || {},
    };
    const nextStore = {
      ...tabFilterStore,
      [activeDashboardTab]: {
        ...(tabFilterStore[activeDashboardTab] || {}),
        auto_filters: pendingFilters || {},
        custom_filter_values: pendingCustomValues || {},
        tab_filter_values: pendingTabFilterValues || {},
        tab_filter_definitions: Array.isArray(tabFilterStore?.[activeDashboardTab]?.tab_filter_definitions)
          ? tabFilterStore[activeDashboardTab].tab_filter_definitions
          : [],
        presets: [...existing, preset],
      },
    };
    void persistTabFilterStore(nextStore);
    setSelectedPresetId(preset.id);
    antMessage.success(`Saved preset "${nextName}"`);
  }, [activeDashboardTab, activeTabPresets, pendingCustomValues, pendingFilters, pendingTabFilterValues, persistTabFilterStore, tabFilterStore]);

  const applyPresetById = useCallback((presetId) => {
    if (!activeDashboardTab || !presetId) return;
    const preset = activeTabPresets.find((p) => p.id === presetId);
    if (!preset) return;
    const auto = preset.auto_filters || {};
    const custom = preset.custom_filter_values || {};
    const tabCustom = preset.tab_filter_values || {};
    setPendingFilters(auto);
    setPendingCustomValues(custom);
    setPendingTabFilterValues(tabCustom);
    applyFilterMapsToContext(auto, custom);
    const nextStore = {
      ...tabFilterStore,
      [activeDashboardTab]: {
        ...(tabFilterStore[activeDashboardTab] || {}),
        auto_filters: auto,
        custom_filter_values: custom,
        tab_filter_values: tabCustom,
        tab_filter_definitions: Array.isArray(tabFilterStore?.[activeDashboardTab]?.tab_filter_definitions)
          ? tabFilterStore[activeDashboardTab].tab_filter_definitions
          : [],
        presets: activeTabPresets,
      },
    };
    void persistTabFilterStore(nextStore);
  }, [activeDashboardTab, activeTabPresets, applyFilterMapsToContext, persistTabFilterStore, tabFilterStore]);

  const deleteSelectedPreset = useCallback(() => {
    if (!activeDashboardTab || !selectedPresetId) return;
    const remaining = activeTabPresets.filter((p) => p.id !== selectedPresetId);
    const nextStore = {
      ...tabFilterStore,
      [activeDashboardTab]: {
        ...(tabFilterStore[activeDashboardTab] || {}),
        presets: remaining,
      },
    };
    void persistTabFilterStore(nextStore);
    setSelectedPresetId(null);
    antMessage.success('Preset deleted');
  }, [activeDashboardTab, activeTabPresets, selectedPresetId, persistTabFilterStore, tabFilterStore]);

  const openCreateTabFilter = () => setTabFilterEditor({ open: true, initial: null });
  const openEditTabFilter = (def) => setTabFilterEditor({ open: true, initial: def });
  const closeTabFilterEditor = () => setTabFilterEditor({ open: false, initial: null });

  const submitTabFilter = async (def) => {
    if (!activeDashboardTab) return;
    const existing = Array.isArray(tabFilterStore?.[activeDashboardTab]?.tab_filter_definitions)
      ? tabFilterStore[activeDashboardTab].tab_filter_definitions
      : [];
    const nextDef = {
      ...(tabFilterEditor.initial || {}),
      ...def,
      id: tabFilterEditor.initial?.id || `tf_${Date.now()}`,
    };
    const nextDefs = tabFilterEditor.initial
      ? existing.map((d) => (d.id === tabFilterEditor.initial.id ? nextDef : d))
      : [...existing, nextDef];

    const nextStore = {
      ...tabFilterStore,
      [activeDashboardTab]: {
        ...(tabFilterStore[activeDashboardTab] || {}),
        auto_filters: pendingFilters || {},
        custom_filter_values: pendingCustomValues || {},
        tab_filter_values: pendingTabFilterValues || {},
        tab_filter_definitions: nextDefs,
        presets: Array.isArray(tabFilterStore?.[activeDashboardTab]?.presets)
          ? tabFilterStore[activeDashboardTab].presets
          : [],
      },
    };
    await persistTabFilterStore(nextStore);
    setTabFilterEditor({ open: false, initial: null });
  };

  const deleteTabFilter = async (filterId) => {
    if (!activeDashboardTab || !filterId) return;
    const existing = Array.isArray(tabFilterStore?.[activeDashboardTab]?.tab_filter_definitions)
      ? tabFilterStore[activeDashboardTab].tab_filter_definitions
      : [];
    const nextDefs = existing.filter((d) => d.id !== filterId);
    const nextVals = { ...(pendingTabFilterValues || {}) };
    delete nextVals[filterId];
    setPendingTabFilterValues(nextVals);

    const nextStore = {
      ...tabFilterStore,
      [activeDashboardTab]: {
        ...(tabFilterStore[activeDashboardTab] || {}),
        auto_filters: pendingFilters || {},
        custom_filter_values: pendingCustomValues || {},
        tab_filter_values: nextVals,
        tab_filter_definitions: nextDefs,
        presets: Array.isArray(tabFilterStore?.[activeDashboardTab]?.presets)
          ? tabFilterStore[activeDashboardTab].presets
          : [],
      },
    };
    await persistTabFilterStore(nextStore);
  };

  const setPending = (col, values) => {
    setPendingFilters(prev => ({ ...prev, [col]: values }));
  };

  const setPendingCustom = (id, values) => {
    setPendingCustomValues(prev => ({ ...prev, [id]: values }));
  };

  if (allColumns.length === 0) return null;

  // Collapsed: chevron + filter icon + active value pills
  if (!isExpanded) {
    return (
      <div className="dashboard-filter-bar dashboard-filter-bar-collapsed" onClick={() => setIsExpanded(true)}>
        <Tooltip title="Expand dashboard filters">
          <button
            type="button"
            className="dx-filter-scope dx-filter-scope-toggle"
            aria-label="Expand dashboard filters"
            onClick={(e) => { e.stopPropagation(); setIsExpanded(true); }}
          >
            <DownOutlined className="dx-filter-scope-chevron" />
            <FilterOutlined />
          </button>
        </Tooltip>
        <div className="dx-filter-collapsed-pills" style={{ flex: 1 }}>
          {activeFilters.length > 0 ? (
            activeFilters.map(([col, values]) => {
              const display = values.length <= 2
                ? values.join(', ')
                : `${values[0]}, ${values[1]} +${values.length - 2}`;
              // Show the SQL operator between column and values so the
              // pill reads naturally (e.g. "TEAM_NAME = 404DNF",
              // "STATUS IN OPEN, CLOSED", "ACCEPTED_AT >= 2024-01-01").
              const op = whereColumnOperators[col] || (values.length > 1 ? 'IN' : '=');
              return (
                <Tag
                  key={col}
                  closable
                  onClose={(e) => { e.stopPropagation(); setDashboardFilter(col, []); }}
                  onClick={(e) => e.stopPropagation()}
                  className="dx-filter-pill dx-filter-pill-modified"
                  style={{ margin: 0, cursor: 'pointer' }}
                  title={`${col} ${op} ${values.join(', ')}`}
                >
                  <span className="dx-filter-pill-key">{col}</span>
                  <span className="dx-filter-pill-op">{op}</span>
                  <span className="dx-filter-pill-val">{display}</span>
                </Tag>
              );
            })
          ) : (
            <span style={{ fontSize: 11, color: 'var(--theme-textSecondary)' }}>No active filters · click to add</span>
          )}
        </div>
        {activeCount > 0 && (
          <span className="dashboard-filter-badge" style={{ flexShrink: 0 }}>{activeCount} active</span>
        )}
        <CustomFilterEditorModal
          open={customEditor.open}
          initial={customEditor.initial}
          onCancel={closeCustomEditor}
          onSubmit={submitCustom}
        />
      </div>
    );
  }

  // Expanded: full filter UI
  return (
    <div className="dashboard-filter-bar dashboard-filter-bar-expanded">
      <div className="dashboard-filter-bar-header">
        <Tooltip title="Collapse dashboard filters">
          <button
            type="button"
            className="dx-filter-scope dx-filter-scope-toggle"
            aria-label="Collapse dashboard filters"
            onClick={() => setIsExpanded(false)}
          >
            <UpOutlined className="dx-filter-scope-chevron" />
            <FilterOutlined />
          </button>
        </Tooltip>
        {activeCount > 0 && <span className="dashboard-filter-badge">{activeCount} active</span>}
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <Select
            size="small"
            placeholder="Tab preset"
            value={selectedPresetId}
            onChange={(value) => {
              setSelectedPresetId(value);
              applyPresetById(value);
            }}
            options={activeTabPresets.map((p) => ({ value: p.id, label: p.name }))}
            style={{ minWidth: 150 }}
            allowClear
          />
          <Tooltip title="Save current filters as tab preset">
            <Button
              size="middle"
              type="text"
              icon={<SaveOutlined style={{ fontSize: 15 }} />}
              onClick={saveCurrentAsPreset}
              aria-label="Save tab preset"
            />
          </Tooltip>
          <Tooltip title="Delete selected preset">
            <Button
              size="middle"
              type="text"
              icon={<DeleteOutlined style={{ fontSize: 15 }} />}
              onClick={deleteSelectedPreset}
              disabled={!selectedPresetId}
              aria-label="Delete tab preset"
            />
          </Tooltip>
          <Tooltip title="Clear all filters">
            <Button
              size="middle"
              type="text"
              icon={<ClearOutlined style={{ fontSize: 16 }} />}
              onClick={clearAll}
              disabled={activeCount === 0 && !isDirty}
              aria-label="Clear all filters"
            />
          </Tooltip>
          <Tooltip title="Apply filters">
            <Button
              size="middle"
              type="text"
              icon={<CaretRightOutlined style={{ fontSize: 18, color: isDirty ? 'var(--theme-primary)' : undefined }} />}
              onClick={applyPending}
              disabled={!isDirty}
              aria-label="Apply filters"
            />
          </Tooltip>
          {/* +Add user-defined filter lives inside the User-defined Filters
              section header below (edit mode only). */}
        </div>
      </div>
      {(visibleColumns.length > 0 || (isEditMode && allColumns.length > 0)) && (
        <div className="dashboard-filter-section" style={{ order: 2, borderTop: '1px dashed var(--theme-border)', paddingTop: 6, marginTop: 4 }}>
          <div className="dashboard-filter-section-header">
            <span className="dashboard-filter-section-title">Auto-generated Filters</span>
            {isEditMode && (
              <span className="dashboard-filter-section-hint">
                {visibleColumns.length} shown · {allColumns.length - visibleColumns.length} hidden
              </span>
            )}
          </div>
        <div className="dashboard-filter-selects">
          {visibleColumns.map(col => {
            const opts = (columnValues[col] || []).map(v => ({ label: v, value: v }));
            const allValues = opts.map(o => o.value);
            const currentVal = pendingFilters[col] || [];
            const isWhereCol = whereFilteredColumns.has(col);
            const whereType = whereColumnTypes[col]; // 'in' | 'eq' | 'bool' | 'dateadd' | 'isnull' | 'isnotnull' | ...
            const whereLabel = whereColumnLabels[col] || col;
            const isReadonly = !!whereColumnReadonly[col];
            // Read-only predicates (IS NULL / IS NOT NULL / opaque) have
            // no editable RHS — hide them from the dashboard filter bar.
            // They remain visible in the SQL modal. BETWEEN renders as
            // two paired AutoComplete inputs (lo, hi).
            if (isReadonly) return null;
            const isBetween = whereType === 'between';
            // Single-value modes: eq, bool, dateadd, cmp, like
            // Multi-value modes: in, or any non-WHERE column (client-side filter)
            const isSingleValue = isWhereCol && whereType && whereType !== 'in' && !isBetween;

            return (
              <div key={col} style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 160 }}>
                <span style={{ fontSize: 13, color: 'var(--theme-primary)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                  {whereLabel}
                  {isWhereCol && (
                    <Tooltip title={`This column is filtered via SQL WHERE (${whereType || 'unknown'}) — a value is required; type any value to override`}>
                      <span style={{ marginLeft: 4, color: 'var(--dx-warn)', cursor: 'help' }}>*</span>
                    </Tooltip>
                  )}
                  {isEditMode && (
                    <Tooltip title="Hide this filter from the dashboard bar">
                      <Button
                        size="small"
                        type="text"
                        icon={<CloseOutlined style={{ fontSize: 10 }} />}
                        onClick={() => setFilterVisibility(col, false)}
                        aria-label={`Hide ${col} filter`}
                        style={{ marginLeft: 'auto', height: 18, width: 18, minWidth: 18, padding: 0, color: 'var(--theme-textSecondary)' }}
                      />
                    </Tooltip>
                  )}
                </span>
                {isBetween ? (
                  // BETWEEN x AND y → two AutoComplete inputs joined by "and".
                  // Pending value is stored as [lo, hi]; both are required.
                  // When nothing is pending, fall back to the parsed bounds
                  // so users see the current range and can edit either side.
                  (() => {
                    const defaults = whereColumnDefaults[col] || [];
                    const lo = currentVal[0] != null ? String(currentVal[0]) : (defaults[0] != null ? String(defaults[0]) : '');
                    const hi = currentVal[1] != null ? String(currentVal[1]) : (defaults[1] != null ? String(defaults[1]) : '');
                    const handleBetween = (idx, v) => {
                      if (v == null || v === '') {
                        warnFor(col, 'Required — enter a value');
                        return;
                      }
                      const base = currentVal.length >= 2 ? currentVal : [lo, hi];
                      const next = [...base];
                      next[idx] = v;
                      setPending(col, next);
                    };
                    return (
                      <div style={{ display: 'flex', gap: 4, alignItems: 'center', width: '100%' }}>
                        <AutoComplete
                          value={lo}
                          options={opts}
                          onChange={(v) => handleBetween(0, v)}
                          placeholder="from"
                          style={{ flex: 1, minWidth: 70 }}
                          allowClear
                          popupMatchSelectWidth={false}
                        />
                        <span style={{ fontSize: 12, color: 'var(--theme-textSecondary)' }}>and</span>
                        <AutoComplete
                          value={hi}
                          options={opts}
                          onChange={(v) => handleBetween(1, v)}
                          placeholder="to"
                          style={{ flex: 1, minWidth: 70 }}
                          allowClear
                          popupMatchSelectWidth={false}
                        />
                      </div>
                    );
                  })()
                ) : isSingleValue ? (
                  // Single-value WHERE column (= , like, dateadd, etc.) — proper
                  // single-value combobox: shows one literal, allows typing a new
                  // value or picking from suggestions.
                  <AutoComplete
                    value={currentVal[0] || ''}
                    options={opts}
                    onChange={(v) => {
                      if (isWhereCol && (v == null || v === '')) {
                        warnFor(col, 'Required — enter a value');
                        setPending(col, []);
                        return;
                      }
                      setPending(col, v ? [v] : []);
                    }}
                    placeholder={`Type or select ${col.toLowerCase()}...`}
                    style={{ width: '100%' }}
                    allowClear
                    filterOption={(input, option) =>
                      String(option?.value ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                  />
                ) : (
                  <Select
                    mode={isWhereCol ? 'tags' : 'multiple'}
                    placeholder={isWhereCol ? `Type or select ${col.toLowerCase()}...` : `Select ${col.toLowerCase()}...`}
                    value={currentVal}
                    options={opts}
                    onChange={(values) => {
                      const arr = Array.isArray(values) ? values : (values != null ? [values] : []);
                      setPending(col, arr);
                    }}
                    style={{ width: '100%' }}
                    allowClear
                    showSearch
                    maxTagCount={1}
                    maxTagPlaceholder={(omitted) => `+${omitted.length}`}
                    popupMatchSelectWidth={false}
                    dropdownRender={(menu) => (
                      <div>
                        <div style={{ display: 'flex', gap: 4, padding: '6px 8px', borderBottom: '1px solid var(--theme-border)' }}>
                          <Button
                            size="small"
                            type="link"
                            style={{ padding: 0, fontSize: 11 }}
                            onClick={() => setPending(col, allValues)}
                            disabled={currentVal.length === allValues.length}
                          >
                            Select all
                          </Button>
                          <Button
                            size="small"
                            type="link"
                            style={{ padding: 0, fontSize: 11 }}
                            onClick={() => setPending(col, [])}
                            disabled={currentVal.length === 0}
                          >
                            Clear
                          </Button>
                          <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--theme-textSecondary)' }}>
                            {currentVal.length}/{allValues.length}
                          </span>
                        </div>
                        {menu}
                      </div>
                    )}
                  />
                )}
                {warnings[col] && (
                  <span style={{ fontSize: 11, color: '#faad14', marginTop: 1 }}>
                    ⚠ {warnings[col]}
                  </span>
                )}
              </div>
            );
          })}
        </div>
          {isEditMode && (() => {
            const hidden = allColumns.filter((c) => !visibleColumns.includes(c));
            if (hidden.length === 0) return null;
            return (
              <div className="dashboard-filter-hidden-row">
                <span className="dashboard-filter-hidden-label">Hidden:</span>
                {hidden.map((c) => (
                  <Tooltip key={c} title={`Show ${c} in the dashboard filter bar`}>
                    <Tag
                      className="dashboard-filter-hidden-tag"
                      onClick={() => setFilterVisibility(c, true)}
                    >
                      <PlusOutlined style={{ fontSize: 10, marginRight: 2 }} />{c}
                    </Tag>
                  </Tooltip>
                ))}
              </div>
            );
          })()}
        </div>
      )}
      {((customFilterDefs && customFilterDefs.length > 0) || isEditMode) && (
        <div className="dashboard-filter-section" style={{ order: 1 }}>
          <div className="dashboard-filter-section-header">
            <span className="dashboard-filter-section-title">User-defined Filters</span>
            {isEditMode && (
              <Tooltip title="Add a user-defined dashboard filter">
                <Button
                  size="small"
                  type="text"
                  icon={<PlusOutlined style={{ fontSize: 12 }} />}
                  onClick={openCreateCustom}
                  aria-label="Add user-defined filter"
                  style={{ height: 20, padding: '0 4px' }}
                >
                  <span style={{ fontSize: 11 }}>Add</span>
                </Button>
              </Tooltip>
            )}
            {isEditMode && (!customFilterDefs || customFilterDefs.length === 0) && (
              <span className="dashboard-filter-section-hint">None yet — click “Add” to create one.</span>
            )}
          </div>
          {customFilterDefs && customFilterDefs.length > 0 && (
          <div className="dashboard-filter-selects">
          {customFilterDefs.map((def) => {
            const sqlState = sqlFilterOptions[def.id];
            const isSqlSourced = def.value_source?.type === 'sql';
            const fixedOpts = isSqlSourced
              ? (sqlState?.options || [])
              : (Array.isArray(def.value_source?.values)
                  ? def.value_source.values.map((v) => ({ value: v, label: v }))
                  : []);
            const sqlLoading = isSqlSourced && sqlState?.loading;
            const sqlError = isSqlSourced ? sqlState?.error : null;
            const current = pendingCustomValues?.[def.id] ?? customFilterValues?.[def.id] ?? def.default_values ?? [];
            const opU = (def.operator || 'IN').toUpperCase();
            const isBetween = opU === 'BETWEEN';
            const isNullOp = opU === 'IS NULL' || opU === 'IS NOT NULL';
            const isPatternOp = opU === 'LIKE' || opU === 'ILIKE'
              || opU === 'NOT LIKE' || opU === 'NOT ILIKE';
            // Only IN/NOT IN render as multi-select; every other
            // single-value operator (=, !=, comparisons, patterns)
            // collapses to a single AutoComplete.
            const isMultiOp = (opU === 'IN' || opU === 'NOT IN');
            // Resolve control type: prefer the new `control_type` field;
            // fall back to deriving it from the operator for legacy
            // filters that were saved before the field existed.
            const ct = def.control_type
              || (isNullOp ? 'switch'
                  : isBetween ? 'date_range'
                  : isPatternOp ? 'input'
                  : (isMultiOp && def.multi_select !== false ? 'multi_selector' : 'selector'));
            const isDate = ct === 'date';
            const isDateRange = ct === 'date_range';
            const isSingle = !isBetween && !isNullOp && !isDate && !isDateRange
              && !(def.multi_select !== false && isMultiOp);
            return (
              <div key={def.id} style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 180 }}>
                <span style={{ fontSize: 13, color: 'var(--theme-primary)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                  {def.label}
                  {isEditMode && (
                    <>
                      <Tooltip title="Edit filter">
                        <Button size="small" type="text" icon={<EditOutlined style={{ fontSize: 11 }} />} onClick={() => openEditCustom(def)} />
                      </Tooltip>
                  <Popconfirm title={`Delete filter "${def.label}"?`} okText="Delete" okType="danger" onConfirm={() => removeCustomFilter(def.id)}>
                    <Button size="small" type="text" icon={<DeleteOutlined style={{ fontSize: 11 }} />} />
                  </Popconfirm>
                    </>
                  )}
                </span>
                {isNullOp ? (
                  // IS NULL / IS NOT NULL: no value list, just a toggle.
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 4px' }}>
                    <Switch
                      size="small"
                      checked={Array.isArray(current) && current.some((v) => v !== undefined && v !== '' && v !== false)}
                      onChange={(checked) => setPendingCustom(def.id, checked ? ['1'] : [])}
                    />
                    <span style={{ fontSize: 12, color: 'var(--theme-textSecondary)' }}>
                      {`${def.column} ${opU}`}
                    </span>
                  </div>
                ) : isDate ? (
                  <DatePicker
                    size="small"
                    style={{ width: '100%' }}
                    value={current[0] ? dayjs(current[0]) : null}
                    onChange={(d) => setPendingCustom(def.id, d ? [d.format('YYYY-MM-DD')] : [])}
                    allowClear
                  />
                ) : isDateRange ? (
                  <DatePicker.RangePicker
                    size="small"
                    style={{ width: '100%' }}
                    value={current[0] && current[1] ? [dayjs(current[0]), dayjs(current[1])] : null}
                    onChange={(arr) => setPendingCustom(def.id, (arr && arr[0] && arr[1]) ? [arr[0].format('YYYY-MM-DD'), arr[1].format('YYYY-MM-DD')] : [])}
                    allowClear
                  />
                ) : isBetween ? (
                  <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                    <AutoComplete
                      value={current[0] ?? ''}
                      options={fixedOpts}
                      onChange={(v) => setPendingCustom(def.id, [v, current[1]])}
                      placeholder="from"
                      style={{ flex: 1, minWidth: 70 }}
                      allowClear
                    />
                    <span style={{ fontSize: 12 }}>and</span>
                    <AutoComplete
                      value={current[1] ?? ''}
                      options={fixedOpts}
                      onChange={(v) => setPendingCustom(def.id, [current[0], v])}
                      placeholder="to"
                      style={{ flex: 1, minWidth: 70 }}
                      allowClear
                    />
                  </div>
                ) : isSingle ? (
                  <AutoComplete
                    value={current[0] ?? ''}
                    options={fixedOpts}
                    onChange={(v) => setPendingCustom(def.id, v ? [v] : [])}
                    placeholder={
                      sqlLoading
                        ? 'Loading…'
                        : (isPatternOp
                            ? `e.g. ${def.column} pattern (use %)`
                            : `Select ${def.column.toLowerCase()}...`)
                    }
                    style={{ width: '100%' }}
                    allowClear
                    notFoundContent={sqlError ? `SQL error: ${sqlError}` : (sqlLoading ? 'Loading…' : undefined)}
                  />
                ) : (
                  <Select
                    mode={(isSqlSourced || fixedOpts.length > 0) ? 'multiple' : 'tags'}
                    placeholder={sqlLoading ? 'Loading…' : `Select ${def.column.toLowerCase()}...`}
                    value={current}
                    options={fixedOpts}
                    onChange={(vals) => setPendingCustom(def.id, Array.isArray(vals) ? vals : (vals ? [vals] : []))}
                    style={{ width: '100%' }}
                    allowClear
                    showSearch
                    maxTagCount={2}
                    loading={sqlLoading}
                    notFoundContent={sqlError ? `SQL error: ${sqlError}` : (sqlLoading ? 'Loading…' : undefined)}
                    onDropdownVisibleChange={(open) => {
                      if (open && isSqlSourced) {
                        fetchSqlFilterOptions(def, { force: !!sqlError || (!sqlState || sqlState.options?.length === 0) });
                      }
                    }}
                  />
                )}
              </div>
            );
          })}
          </div>
          )}
        </div>
      )}
      {((activeTabFilterDefs && activeTabFilterDefs.length > 0) || isEditMode) && (
        <div className="dashboard-filter-section" style={{ order: 1 }}>
          <div className="dashboard-filter-section-header">
            <span className="dashboard-filter-section-title">Tab Filters ({activeDashboardTab || 'Overview'})</span>
            {isEditMode && (
              <Tooltip title="Add a tab-level filter">
                <Button
                  size="small"
                  type="text"
                  icon={<PlusOutlined style={{ fontSize: 12 }} />}
                  onClick={openCreateTabFilter}
                  aria-label="Add tab-level filter"
                  style={{ height: 20, padding: '0 4px' }}
                >
                  <span style={{ fontSize: 11 }}>Add</span>
                </Button>
              </Tooltip>
            )}
            {isEditMode && (!activeTabFilterDefs || activeTabFilterDefs.length === 0) && (
              <span className="dashboard-filter-section-hint">No tab filters yet.</span>
            )}
          </div>
          {activeTabFilterDefs && activeTabFilterDefs.length > 0 && (
            <div className="dashboard-filter-selects">
              {activeTabFilterDefs.map((def) => {
                const sqlState = sqlFilterOptions[def.id];
                const isSqlSourced = def.value_source?.type === 'sql';
                const fixedOpts = isSqlSourced
                  ? (sqlState?.options || [])
                  : (Array.isArray(def.value_source?.values)
                      ? def.value_source.values.map((v) => ({ value: v, label: v }))
                      : []);
                const sqlLoading = isSqlSourced && sqlState?.loading;
                const sqlError = isSqlSourced ? sqlState?.error : null;
                const current = pendingTabFilterValues?.[def.id] ?? [];
                const opU = (def.operator || 'IN').toUpperCase();
                const isBetween = opU === 'BETWEEN';
                const isNullOp = opU === 'IS NULL' || opU === 'IS NOT NULL';
                const isPatternOp = opU === 'LIKE' || opU === 'ILIKE'
                  || opU === 'NOT LIKE' || opU === 'NOT ILIKE';
                const isMultiOp = (opU === 'IN' || opU === 'NOT IN');
                const ct = def.control_type
                  || (isNullOp ? 'switch'
                      : isBetween ? 'date_range'
                      : isPatternOp ? 'input'
                      : (isMultiOp && def.multi_select !== false ? 'multi_selector' : 'selector'));
                const isDate = ct === 'date';
                const isDateRange = ct === 'date_range';
                const isSingle = !isBetween && !isNullOp && !isDate && !isDateRange
                  && !(def.multi_select !== false && isMultiOp);

                return (
                  <div key={def.id} style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 180 }}>
                    <span style={{ fontSize: 13, color: 'var(--theme-primary)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                      {def.label}
                      {isEditMode && (
                        <>
                          <Tooltip title="Edit tab filter">
                            <Button size="small" type="text" icon={<EditOutlined style={{ fontSize: 11 }} />} onClick={() => openEditTabFilter(def)} />
                          </Tooltip>
                          <Popconfirm title={`Delete tab filter "${def.label}"?`} okText="Delete" okType="danger" onConfirm={() => deleteTabFilter(def.id)}>
                            <Button size="small" type="text" icon={<DeleteOutlined style={{ fontSize: 11 }} />} />
                          </Popconfirm>
                        </>
                      )}
                    </span>
                    {isNullOp ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 4px' }}>
                        <Switch
                          size="small"
                          checked={Array.isArray(current) && current.some((v) => v !== undefined && v !== '' && v !== false)}
                          onChange={(checked) => setPendingTabCustom(def.id, checked ? ['1'] : [])}
                        />
                        <span style={{ fontSize: 12, color: 'var(--theme-textSecondary)' }}>
                          {`${def.column} ${opU}`}
                        </span>
                      </div>
                    ) : isDate ? (
                      <DatePicker
                        size="small"
                        style={{ width: '100%' }}
                        value={current[0] ? dayjs(current[0]) : null}
                        onChange={(d) => setPendingTabCustom(def.id, d ? [d.format('YYYY-MM-DD')] : [])}
                        allowClear
                      />
                    ) : isDateRange ? (
                      <DatePicker.RangePicker
                        size="small"
                        style={{ width: '100%' }}
                        value={current[0] && current[1] ? [dayjs(current[0]), dayjs(current[1])] : null}
                        onChange={(arr) => setPendingTabCustom(def.id, (arr && arr[0] && arr[1]) ? [arr[0].format('YYYY-MM-DD'), arr[1].format('YYYY-MM-DD')] : [])}
                        allowClear
                      />
                    ) : isBetween ? (
                      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                        <AutoComplete
                          value={current[0] ?? ''}
                          options={fixedOpts}
                          onChange={(v) => setPendingTabCustom(def.id, [v, current[1]])}
                          placeholder="from"
                          style={{ flex: 1, minWidth: 70 }}
                          allowClear
                        />
                        <span style={{ fontSize: 12 }}>and</span>
                        <AutoComplete
                          value={current[1] ?? ''}
                          options={fixedOpts}
                          onChange={(v) => setPendingTabCustom(def.id, [current[0], v])}
                          placeholder="to"
                          style={{ flex: 1, minWidth: 70 }}
                          allowClear
                        />
                      </div>
                    ) : isSingle ? (
                      <AutoComplete
                        value={current[0] ?? ''}
                        options={fixedOpts}
                        onChange={(v) => setPendingTabCustom(def.id, v ? [v] : [])}
                        placeholder={
                          sqlLoading
                            ? 'Loading…'
                            : (isPatternOp
                                ? `e.g. ${def.column} pattern (use %)`
                                : `Select ${def.column.toLowerCase()}...`)
                        }
                        style={{ width: '100%' }}
                        allowClear
                        notFoundContent={sqlError ? `SQL error: ${sqlError}` : (sqlLoading ? 'Loading…' : undefined)}
                      />
                    ) : (
                      <Select
                        mode={(isSqlSourced || fixedOpts.length > 0) ? 'multiple' : 'tags'}
                        placeholder={sqlLoading ? 'Loading…' : `Select ${def.column.toLowerCase()}...`}
                        value={current}
                        options={fixedOpts}
                        onChange={(vals) => setPendingTabCustom(def.id, Array.isArray(vals) ? vals : (vals ? [vals] : []))}
                        style={{ width: '100%' }}
                        allowClear
                        showSearch
                        maxTagCount={2}
                        loading={sqlLoading}
                        notFoundContent={sqlError ? `SQL error: ${sqlError}` : (sqlLoading ? 'Loading…' : undefined)}
                        onDropdownVisibleChange={(open) => {
                          if (open && isSqlSourced) {
                            fetchSqlFilterOptions(def, { force: !!sqlError || (!sqlState || sqlState.options?.length === 0) });
                          }
                        }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      <CustomFilterEditorModal
        open={customEditor.open}
        initial={customEditor.initial}
        onCancel={closeCustomEditor}
        onSubmit={submitCustom}
      />
      <CustomFilterEditorModal
        open={tabFilterEditor.open}
        initial={tabFilterEditor.initial}
        onCancel={closeTabFilterEditor}
        onSubmit={submitTabFilter}
      />
    </div>
  );
}

/**
 * Filter bar shown inside a dashboard card — delegates to SqlWhereFilterBar from DeliveryFilters
 * but uses DashboardCard's own loadData (cache-aware) instead of the generic executeDashboardSQL.
 */
function ComponentFilterBar({ parsedFilters, filterValues, disabledFilters, data, onFilterChange, onToggleDisabled, onReset }) {
  // Read-only predicates (IS NULL / IS NOT NULL / opaque shapes) have no
  // editable RHS \u2014 they are excluded from the chip row entirely. They
  // remain visible in the SQL modal.
  const filterEntries = Object.entries(parsedFilters).filter(([, def]) => !def.readonly);
  const [expanded, setExpanded] = useState(false);
  // Pending (uncommitted) per-card filter values — committed via the Run
  // button so user changes don't fire one SQL re-execution per keystroke.
  const [pending, setPending] = useState(filterValues);
  // Pending (uncommitted) disabled-filter map — same Run-button commit
  // semantics as `pending` values above.
  const [pendingDisabled, setPendingDisabled] = useState(disabledFilters || {});
  // Per-column inline warning (auto-clears after ~2.5s) — shown in place
  // of a global toast so the message fits the dense filter row.
  const [warnings, setWarnings] = useState({});
  const warnFor = useCallback((col, msg) => {
    setWarnings(prev => ({ ...prev, [col]: msg }));
    setTimeout(() => setWarnings(prev => {
      if (prev[col] !== msg) return prev;
      const next = { ...prev }; delete next[col]; return next;
    }), 2500);
  }, []);
  // Re-sync pending when committed filterValues change from outside (e.g. reset).
  useEffect(() => { setPending(filterValues); }, [filterValues]);
  useEffect(() => { setPendingDisabled(disabledFilters || {}); }, [disabledFilters]);

  if (filterEntries.length === 0) return null;

  const committedDisabled = disabledFilters || {};
  const isPendingDisabled = (col) => !!pendingDisabled[col];
  const isCommittedDisabled = (col) => !!committedDisabled[col];

  const valueIsDirty = filterEntries.some(([col, def]) => {
    if (isPendingDisabled(col)) return false; // value irrelevant when off
    const cur = pending[col] || def.values;
    const com = filterValues[col] || def.values;
    return JSON.stringify([...cur].sort()) !== JSON.stringify([...com].sort());
  });
  const disabledIsDirty = filterEntries.some(([col]) => isPendingDisabled(col) !== isCommittedDisabled(col));
  const isDirty = valueIsDirty || disabledIsDirty;
  const runPending = () => {
    // Commit pending value changes first
    filterEntries.forEach(([col, def]) => {
      // Skip when pending-disabled (value is irrelevant), but still
      // commit the disable toggle below.
      if (isPendingDisabled(col)) return;
      const cur = pending[col] || def.values;
      const com = filterValues[col] || def.values;
      if (JSON.stringify([...cur].sort()) !== JSON.stringify([...com].sort())) {
        onFilterChange(col, cur);
      }
    });
    // Then commit any disable-toggle changes
    if (onToggleDisabled) {
      filterEntries.forEach(([col]) => {
        const before = isCommittedDisabled(col);
        const after = isPendingDisabled(col);
        if (before !== after) onToggleDisabled(col, after);
      });
    }
  };
  const setPendingFor = (col, vals) => setPending(prev => ({ ...prev, [col]: vals }));
  const togglePendingDisabled = (col, next) => setPendingDisabled(prev => {
    const out = { ...prev };
    if (next) out[col] = true; else delete out[col];
    return out;
  });

  const getOptions = (col, def) => {
    if (def.type === 'dateadd') {
      const presets = ['-1', '-7', '-14', '-30', '-60', '-90'];
      const allVals = new Set([...presets, ...def.values.map(String)]);
      return [...allVals].sort((a, b) => Number(a) - Number(b)).map(v => {
        const abs = Math.abs(Number(v));
        return { label: `Last ${abs} day${abs !== 1 ? 's' : ''}`, value: v };
      });
    }
    if (def.type === 'bool') {
      return [{ label: 'TRUE', value: 'TRUE' }, { label: 'FALSE', value: 'FALSE' }];
    }
    const optSet = new Set(def.values.map(String));
    if (data?.columns && data?.rows) {
      const colIdx = data.columns.findIndex(c => c.toUpperCase() === col);
      if (colIdx >= 0) {
        for (const row of data.rows) {
          if (optSet.size >= 200) break;
          if (row[colIdx] != null) optSet.add(String(row[colIdx]));
        }
      }
    }
    return [...optSet].sort((a, b) => {
      const na = Number(a), nb = Number(b);
      if (!isNaN(na) && !isNaN(nb)) return na - nb;
      return a.localeCompare(b);
    }).map(v => ({ label: v, value: v }));
  };

  const hasChanges = filterEntries.some(([col, def]) => {
    if (isCommittedDisabled(col)) return true;
    const current = filterValues[col] || def.values;
    return JSON.stringify([...current].sort()) !== JSON.stringify([...def.values].sort());
  });

  const modifiedCount = filterEntries.filter(([col, def]) => {
    if (isCommittedDisabled(col)) return true;
    const current = filterValues[col] || def.values;
    return JSON.stringify([...current].sort()) !== JSON.stringify([...def.values].sort());
  }).length;

  if (!expanded) {
    const activeChips = filterEntries
      .map(([col, def]) => {
        const current = filterValues[col] || def.values;
        const modified = JSON.stringify([...current].sort()) !== JSON.stringify([...def.values].sort());
        const off = isCommittedDisabled(col);
        return { col, def, current, modified, off };
      })
      .filter(({ current, off }) => off || (Array.isArray(current) && current.length > 0));

    return (
      <div className="dashboard-card-where-filters dashboard-card-where-filters-collapsed">
        <Tooltip title={`${filterEntries.length} card filter${filterEntries.length !== 1 ? 's' : ''}${modifiedCount ? ` · ${modifiedCount} modified` : ''} — click to expand`}>
          <button
            type="button"
            className="dx-filter-scope where dx-filter-scope-toggle"
            aria-label="Expand card filters"
            onClick={() => setExpanded(true)}
          >
            <DownOutlined className="dx-filter-scope-chevron" />
            <FilterOutlined />
            {modifiedCount > 0 && <span className="dx-filter-scope-dot" aria-hidden />}
          </button>
        </Tooltip>
        <div className="dx-filter-collapsed-pills" onClick={() => setExpanded(true)}>
          {activeChips.map(({ col, def, current, modified, off }) => {
            const display = current.length <= 2
              ? current.join(', ')
              : `${current[0]}, ${current[1]} +${current.length - 2}`;
            const op = def.operator || (current.length > 1 ? 'IN' : '=');
            return (
              <span
                key={col}
                className={`dx-filter-pill${modified ? ' dx-filter-pill-modified' : ''}`}
                title={off ? `${col} — OFF` : `${col} ${op} ${current.join(', ')}`}
                style={off ? { opacity: 0.55, textDecoration: 'line-through' } : undefined}
              >
                <span className="dx-filter-pill-key">{col}</span>
                {!off && <span className="dx-filter-pill-op">{op}</span>}
                {!off && <span className="dx-filter-pill-val">{display}</span>}
                {off && <span className="dx-filter-pill-op">OFF</span>}
              </span>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-card-where-filters">
      <Tooltip title="Collapse card filters">
        <button
          type="button"
          className="dx-filter-scope where dx-filter-scope-toggle"
          aria-label="Collapse card filters"
          onClick={() => setExpanded(false)}
        >
          <UpOutlined className="dx-filter-scope-chevron" />
          <FilterOutlined />
        </button>
      </Tooltip>
      <div className="dashboard-card-where-filters-fields">
      {filterEntries.map(([col, def]) => {
        const opts = getOptions(col, def);
        const currentVal = pending[col] || def.values;
        const off = isPendingDisabled(col);
        // BETWEEN/NOT BETWEEN need two inputs (lo, hi). Everything else
        // except IN/bool is rendered as a single AutoComplete.
        const isBetween = def.type === 'between';
        const isSingleValue = !isBetween && def.type !== 'in' && def.type !== 'bool';
        const label = def.displayLabel || col;
        const requiredMarker = (
          <Tooltip title={`This column is filtered via SQL WHERE (${def.type}) — a value is required.`}>
            <span style={{ marginLeft: 3, color: 'var(--dx-warn, #faad14)', cursor: 'help' }}>*</span>
          </Tooltip>
        );
        const toggleBtn = null;
        const wrapperStyle = {
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          opacity: off ? 0.55 : 1,
        };
        const labelEl = (
          <span style={{ fontSize: 10, color: 'var(--theme-primary, #1890ff)', fontWeight: 600, lineHeight: 1.2, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            {toggleBtn}
            <span style={{ textDecoration: off ? 'line-through' : 'none' }}>{label}</span>
            {!off && requiredMarker}
          </span>
        );
        if (isBetween) {
          const lo = currentVal[0] != null ? String(currentVal[0]) : '';
          const hi = currentVal[1] != null ? String(currentVal[1]) : '';
          const handleBetween = (idx, v) => {
            if (v == null || v === '') {
              warnFor(col, 'Required — reverted to default');
              setPendingFor(col, def.values);
              return;
            }
            const next = [...currentVal];
            while (next.length < 2) next.push(def.values[next.length]);
            next[idx] = v;
            setPendingFor(col, next);
          };
          return (
            <div key={col} style={wrapperStyle}>
              {labelEl}
              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                <AutoComplete
                  value={lo}
                  options={opts}
                  onChange={(v) => handleBetween(0, v)}
                  placeholder="from"
                  size="small"
                  style={{ minWidth: 80, maxWidth: 120 }}
                  allowClear
                  popupMatchSelectWidth={false}
                  disabled={off}
                />
                <span style={{ fontSize: 10, color: 'var(--theme-textSecondary, #888)' }}>and</span>
                <AutoComplete
                  value={hi}
                  options={opts}
                  onChange={(v) => handleBetween(1, v)}
                  placeholder="to"
                  size="small"
                  style={{ minWidth: 80, maxWidth: 120 }}
                  allowClear
                  popupMatchSelectWidth={false}
                  disabled={off}
                />
              </div>
              {warnings[col] && !off && (
                <span style={{ fontSize: 10, color: '#faad14', lineHeight: 1.2 }}>⚠ {warnings[col]}</span>
              )}
            </div>
          );
        }
        if (isSingleValue) {
          return (
            <div key={col} style={wrapperStyle}>
              {labelEl}
              <AutoComplete
                value={currentVal[0] != null ? String(currentVal[0]) : ''}
                options={opts}
                onChange={(v) => {
                  if (v == null || v === '') {
                    warnFor(col, 'Required — reverted to default');
                    setPendingFor(col, def.values);
                    return;
                  }
                  setPendingFor(col, [v]);
                }}
                placeholder={`Type or pick ${col}…`}
                style={{ minWidth: 110, maxWidth: 220 }}
                size="small"
                allowClear
                popupMatchSelectWidth={false}
                disabled={off}
                filterOption={(input, option) =>
                  String(option?.value ?? '').toLowerCase().includes(String(input).toLowerCase())
                }
              />
              {warnings[col] && !off && (
                <span style={{ fontSize: 10, color: '#faad14', lineHeight: 1.2 }}>⚠ {warnings[col]}</span>
              )}
            </div>
          );
        }
        return (
          <div key={col} style={wrapperStyle}>
            {labelEl}
            <Select
              mode={def.type === 'bool' ? undefined : 'multiple'}
              value={def.type === 'bool' ? currentVal[0] : currentVal}
              options={opts}
              onChange={(v) => {
                if (def.type === 'bool') {
                  setPendingFor(col, [v]);
                } else {
                  const arr = Array.isArray(v) ? v : [v];
                  if (arr.length === 0) {
                    warnFor(col, 'Required — reverted to default');
                    setPendingFor(col, def.values);
                    return;
                  }
                  setPendingFor(col, arr);
                }
              }}
              style={{ minWidth: 110, maxWidth: 220 }}
              size="small"
              showSearch
              allowClear={false}
              popupMatchSelectWidth={false}
              maxTagCount={1}
              maxTagPlaceholder={(omitted) => `+${omitted.length}`}
              disabled={off}
            />
            {warnings[col] && !off && (
              <span style={{ fontSize: 10, color: '#faad14', lineHeight: 1.2 }}>⚠ {warnings[col]}</span>
            )}
          </div>
        );
      })}
      </div>
      <div className="dashboard-card-where-filters-actions">
        <Tooltip title={isDirty ? 'Run filters' : 'No pending changes'}>
          <Button
            size="small"
            type="text"
            icon={<CaretRightOutlined style={{ fontSize: 14, color: isDirty ? 'var(--theme-primary)' : undefined }} />}
            onClick={runPending}
            disabled={!isDirty}
            aria-label="Run card filters"
          />
        </Tooltip>
        {hasChanges && (
          <Tooltip title="Reset to original SQL values">
            <Button
              size="small"
              type="text"
              icon={<ClearOutlined style={{ fontSize: 11 }} />}
              onClick={onReset}
              aria-label="Reset card filters"
            />
          </Tooltip>
        )}
      </div>
    </div>
  );
}

/**
 * A single rendered dashboard card. Uses cached data from context when available,
 * re-executes SQL only on first load or manual refresh.
 */
function DashboardCard({ item, onRemove, isVisible, isFocused, onFocus, onEditItem, onMoveToTab, availableTabs = [], canEdit = true, isInteracting = false }) {
  const {
    getCachedData, setCachedData, dashboardFilters, registerItemColumns,
    customFilterDefs, customFilterValues, itemColumnData, updateItem, dashboards, activeDashboardId, activeDashboardTab,
  } = useDashboard();
  const [data, setData] = useState(() => getCachedData(item.id));
  const [loading, setLoading] = useState(!getCachedData(item.id));
  const [error, setError] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Parsed WHERE filter definitions (column name → { type, values, isString, ... })
  const parsedWhereFilters = useMemo(() => parseWhereFilters(item.sql_query), [item.sql_query]);

  // User-selected values for each WHERE parameter (initialized from SQL)
  const [componentSqlFilters, setComponentSqlFilters] = useState(() =>
    Object.fromEntries(Object.entries(parseWhereFilters(item.sql_query)).map(([col, def]) => [col, def.values]))
  );
  // Per-card map of WHERE filters the user has toggled OFF. Disabled
  // filters are rewritten to `1=1` by rebuildSqlWithFilters so the
  // surrounding predicate structure stays valid.
  const [componentDisabledFilters, setComponentDisabledFilters] = useState({});

  // SQL with the current per-card WHERE-filter overrides applied AND
  // wrapped with dashboard-level custom filters (auto-skipped for cards
  // whose result-set lacks the configured column).
  const cardColumns = itemColumnData[item.id]?.columns || data?.columns || null;
  const activeDashSummary = useMemo(
    () => dashboards.find((d) => d.id === activeDashboardId) || null,
    [dashboards, activeDashboardId]
  );
  const activeTabCfg = useMemo(() => {
    const settings = activeDashSummary?.tab_settings;
    if (!settings || typeof settings !== 'object') return {};
    const tabKey = activeDashboardTab || 'Overview';
    const raw = settings[tabKey];
    return raw && typeof raw === 'object' ? raw : {};
  }, [activeDashSummary, activeDashboardTab]);
  const tabLevelFilterDefs = useMemo(
    () => (Array.isArray(activeTabCfg?.tab_filter_definitions) ? activeTabCfg.tab_filter_definitions : []),
    [activeTabCfg]
  );
  const tabLevelFilterValues = useMemo(() => {
    const raw = activeTabCfg?.tab_filter_values;
    return raw && typeof raw === 'object' ? raw : {};
  }, [activeTabCfg]);
  const mergedCustomFilterDefs = useMemo(
    () => [...(customFilterDefs || []), ...tabLevelFilterDefs],
    [customFilterDefs, tabLevelFilterDefs]
  );
  const mergedCustomFilterValues = useMemo(
    () => ({ ...(customFilterValues || {}), ...(tabLevelFilterValues || {}) }),
    [customFilterValues, tabLevelFilterValues]
  );

  // Merge dashboard-level WHERE filters into per-component filter values:
  // for any WHERE column the dashboard is currently driving, the dashboard
  // value wins; otherwise we keep the component's local value. We compute
  // this here (rather than via setComponentSqlFilters in a useEffect) so
  // the merge is part of the SAME render that consumes effectiveSql — a
  // separate setState would cause effectiveSql to change across two
  // commits, firing the load effect twice and cancelling the first fetch.
  // Case-insensitive lookup map of the dashboard-level WHERE filter values,
  // keyed by lower-cased column name. parseWhereFilters returns the column
  // exactly as written in the SQL (which may be lower-case), while the
  // dashboard filter bar keys by the column name from the result-set (which
  // Snowflake uppercases). Without this normalization, the merge below
  // silently fails when the two cases don't match and the inner SQL keeps
  // running with its original WHERE literal — the bug where flipping
  // IS_ACTIVE in the dashboard bar still produced "No data".
  const dashboardFiltersCI = useMemo(() => {
    const m = {};
    Object.entries(dashboardFilters || {}).forEach(([k, v]) => {
      if (v && v.length > 0) m[String(k).toLowerCase()] = v;
    });
    return m;
  }, [dashboardFilters]);

  const mergedComponentFilters = useMemo(() => {
    if (Object.keys(parsedWhereFilters).length === 0) return componentSqlFilters;
    const out = { ...componentSqlFilters };
    Object.entries(parsedWhereFilters).forEach(([id, def]) => {
      const colKey = String(def.column || id).toLowerCase();
      const dv = dashboardFiltersCI[colKey];
      if (dv && dv.length > 0) out[id] = dv;
    });
    return out;
  }, [componentSqlFilters, parsedWhereFilters, dashboardFiltersCI]);

  // Disabled-filter map with dashboard overrides: a dashboard-driven WHERE
  // column re-enables its predicate even if the user had toggled it off
  // locally.
  const mergedDisabledFilters = useMemo(() => {
    if (Object.keys(componentDisabledFilters).length === 0) return componentDisabledFilters;
    const out = { ...componentDisabledFilters };
    Object.entries(parsedWhereFilters).forEach(([id, def]) => {
      const colKey = String(def.column || id).toLowerCase();
      const dv = dashboardFiltersCI[colKey];
      if (dv && dv.length > 0) delete out[id];
    });
    return out;
  }, [componentDisabledFilters, parsedWhereFilters, dashboardFiltersCI]);

  const effectiveSql = useMemo(
    () => {
      const rebuilt = rebuildSqlWithFilters(item.sql_query, parsedWhereFilters, mergedComponentFilters, mergedDisabledFilters);
      return wrapSqlWithDashboardFilters(rebuilt, mergedCustomFilterDefs || [], mergedCustomFilterValues || {}, { cardId: item.id, cardColumns });
    },
    [item.sql_query, item.id, parsedWhereFilters, mergedComponentFilters, mergedDisabledFilters, mergedCustomFilterDefs, mergedCustomFilterValues, cardColumns]
  );

  // Component detail modals
  const [jsonModalOpen, setJsonModalOpen] = useState(false);
  const [sqlModalOpen, setSqlModalOpen] = useState(false);
  const [dataModalOpen, setDataModalOpen] = useState(false);
  const [editJsonModalOpen, setEditJsonModalOpen] = useState(false);
  const [editJsonString, setEditJsonString] = useState('');
  const [editJsonError, setEditJsonError] = useState('');
  const [copiedText, setCopiedText] = useState('');

  // Track which SQL the current `data` corresponds to. When effectiveSql
  // changes (e.g. dashboard-level WHERE filter applied), data is stale and
  // the body should show the skeleton even before loadData has a chance to
  // run. Without this, tables briefly render old rows under a new filter.
  const [loadedSql, setLoadedSql] = useState(null);
  const isStale = data != null && loadedSql != null && loadedSql !== effectiveSql;

  // AbortController ref — cancel in-flight requests on unmount or re-fetch
  const abortRef = useRef(null);

  // loadData accepts an optional sqlOverride; when given, result is NOT cached
  const loadData = useCallback(async (forceRefresh = false, sqlOverride = null) => {
    const sql = sqlOverride || item.sql_query;
    const useCache = !sqlOverride;
    // eslint-disable-next-line no-console
    console.debug('[DBG card]', item.id, 'loadData called', { forceRefresh, hasOverride: !!sqlOverride, sql: sql?.slice(0, 80) });
    // Use cache unless forced refresh or custom SQL
    if (!forceRefresh && useCache) {
      const cached = getCachedData(item.id);
      if (cached) {
        setData(cached);
        setLoadedSql(sql);
        setLoading(false);
        return;
      }
    }
    // Cancel any previous in-flight request for this card
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);
    try {
      const res = await executeDashboardSQL(sql, 200, forceRefresh, { signal: controller.signal });
      setData(res.data);
      setLoadedSql(sql);
      if (useCache) {
        setCachedData(item.id, res.data);
        // Also seed the editor's chart-data cache so opening this item
        // in the Chart Editor reuses the rows we just fetched (otherwise
        // the editor only gets a hit for chart/metric items, since
        // PlotlyChartCard / MetricCard fill that cache themselves).
        if (res.data?.columns && res.data?.rows) {
          setEditorCache(item.id, { columns: res.data.columns, rows: res.data.rows });
        }
      }
      setLoading(false);
    } catch (err) {
      if (err.name === 'AbortError') return; // aborted — don't touch loading state
      setError(err.message || 'Failed to load data');
      setLoading(false);
    }
  }, [item.sql_query, item.id, getCachedData, setCachedData]);

  // Abort in-flight requests when the card unmounts (e.g. switching dashboards)
  useEffect(() => {
    return () => { if (abortRef.current) abortRef.current.abort(); };
  }, []);

  // Load data on mount (cache-first) — no visibility gate so newly added cards load immediately.
  // Gate on `loadedSql` too: when the effectiveSql effect below intentionally
  // clears `data` to trigger a refetch with the wrapped SQL, we must NOT
  // race it here by re-loading with the bare item.sql_query.
  useEffect(() => {
    if (!data && !error && loadedSql == null) {
      loadData();
    }
  }, [data, error, loadedSql, loadData]);

  // Reload when item is updated (e.g. agent update_dashboard_component) — cache was cleared
  const prevSqlRef = useRef(item.sql_query);
  const prevChartRef = useRef(item.chart_config);
  useEffect(() => {
    const sqlChanged = prevSqlRef.current !== item.sql_query;
    const chartChanged = JSON.stringify(prevChartRef.current) !== JSON.stringify(item.chart_config);
    if (sqlChanged || chartChanged) {
      prevSqlRef.current = item.sql_query;
      prevChartRef.current = item.chart_config;
      // Reset component WHERE filters to match the new SQL
      if (sqlChanged) {
        const newParsed = parseWhereFilters(item.sql_query);
        setComponentSqlFilters(Object.fromEntries(Object.entries(newParsed).map(([col, def]) => [col, def.values])));
        // Disabled-filter ids are tied to the previous SQL — clear them
        // so a re-parse doesn't leave dangling off-switches.
        setComponentDisabledFilters({});
      }
      // SQL changed → re-execute; chart-only change → just invalidate to pick up new spec
      setData(null);
      setLoadedSql(null);
      setError(null);
      loadData(true);
    }
  }, [item.sql_query, item.chart_config, loadData]);

  // Reload when dashboard-level custom filter values change. effectiveSql
  // already bakes in the wrapper; we just need to trigger a refetch when
  // the wrapper would produce different SQL. We do NOT null out `data`
  // here — the `isStale` flag (loadedSql !== effectiveSql) drives the
  // skeleton, and nulling would race the mount effect into firing with
  // the unwrapped SQL.
  const prevEffectiveSqlRef = useRef(effectiveSql);
  useEffect(() => {
    if (prevEffectiveSqlRef.current === effectiveSql) return;
    // eslint-disable-next-line no-console
    console.debug('[DBG card]', item.id, 'effectiveSql changed → loadData', { prev: prevEffectiveSqlRef.current, next: effectiveSql });
    prevEffectiveSqlRef.current = effectiveSql;
    setError(null);
    loadData(true, effectiveSql);
  }, [effectiveSql, loadData, item.id]);

  // Handler: user changes a WHERE filter value → rebuild SQL and re-fetch
  const handleComponentFilterChange = useCallback((col, vals) => {
    setComponentSqlFilters(prev => {
      const next = { ...prev, [col]: vals };
      const newSql = rebuildSqlWithFilters(item.sql_query, parsedWhereFilters, next, componentDisabledFilters);
      setData(null);
      setError(null);
      loadData(true, newSql);
      return next;
    });
  }, [item.sql_query, parsedWhereFilters, loadData, componentDisabledFilters]);

  // Handler: user toggles a WHERE filter ON/OFF → rebuild SQL and re-fetch
  const handleToggleComponentFilterDisabled = useCallback((col, nextDisabled) => {
    setComponentDisabledFilters(prev => {
      const next = { ...prev };
      if (nextDisabled) next[col] = true; else delete next[col];
      const newSql = rebuildSqlWithFilters(item.sql_query, parsedWhereFilters, componentSqlFilters, next);
      setData(null);
      setError(null);
      loadData(true, newSql);
      return next;
    });
  }, [item.sql_query, parsedWhereFilters, loadData, componentSqlFilters]);

  // Handler: reset all component WHERE filters to the original SQL values
  const handleResetComponentFilters = useCallback(() => {
    const reset = Object.fromEntries(Object.entries(parsedWhereFilters).map(([col, def]) => [col, def.values]));
    setComponentSqlFilters(reset);
    setComponentDisabledFilters({});
    setData(null);
    setError(null);
    loadData(true);
  }, [parsedWhereFilters, loadData]);

  // Register columns for dashboard-level filter (including WHERE clause columns)
  useEffect(() => {
    if (data?.columns) registerItemColumns(item.id, data.columns, data.rows, parsedWhereFilters);
  }, [data, item.id, registerItemColumns, parsedWhereFilters]);

  // Dashboard-level WHERE-column changes are now merged into effectiveSql
  // via the `mergedComponentFilters` useMemo above, so we don't need a
  // separate sync effect here. (An old version called loadData here too,
  // which raced the effective-SQL effect via the shared abortRef.)

  // Apply dashboard-level filters (client-side for result-set columns only)
  const filteredData = useMemo(() => {
    if (!data?.columns || !data?.rows) return data;
    const active = Object.entries(dashboardFilters).filter(([col, v]) => {
      if (!v || v.length === 0) return false;
      // Skip WHERE clause columns — handled via SQL re-execution above
      if (parsedWhereFilters[col]) return false;
      return true;
    });
    if (active.length === 0) return data;
    const rows = data.rows.filter(row =>
      active.every(([colName, values]) => {
        const ci = data.columns.indexOf(colName);
        if (ci === -1) return true; // column not in this component — skip
        const cell = row[ci] != null ? String(row[ci]) : '(null)';
        return values.includes(cell);
      })
    );
    return { ...data, rows, row_count: rows.length };
  }, [data, dashboardFilters, parsedWhereFilters]);

  const handleDownloadCSV = () => {
    if (!data?.columns || !data?.rows) return;
    const escape = (v) => (v == null ? '' : '"' + String(v).replace(/"/g, '""') + '"');
    // CRLF line endings + UTF-8 BOM so Excel correctly reads non-ASCII text.
    const csv = '﻿' + [
      data.columns.map(escape).join(','),
      ...data.rows.map((r) => r.map(escape).join(',')),
    ].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${item.title.replace(/[^a-z0-9]/gi, '_')}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const handleCopyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedText(label);
      setTimeout(() => setCopiedText(''), 2000);
    });
  };

  const getComponentJson = () => {
    const obj = {
      id: item.id,
      item_type: item.item_type,
      title: item.title,
      sql_query: item.sql_query,
      grid_x: item.grid_x, grid_y: item.grid_y,
      grid_w: item.grid_w, grid_h: item.grid_h,
    };
    if (item.chart_config) obj.chart_config = item.chart_config;
    return JSON.stringify(obj, null, 2);
  };

  const handleOpenEditJson = (e) => {
    e?.stopPropagation?.();
    const obj = {
      item_type: item.item_type,
      title: item.title,
      sql_query: item.sql_query,
      grid_x: item.grid_x, grid_y: item.grid_y,
      grid_w: item.grid_w, grid_h: item.grid_h,
    };
    if (item.chart_config) obj.chart_config = item.chart_config;
    setEditJsonString(JSON.stringify(obj, null, 2));
    setEditJsonError('');
    setEditJsonModalOpen(true);
  };

  const handleSaveEditJson = async () => {
    try {
      const parsed = JSON.parse(editJsonString);
      // Route through the DashboardContext so the in-memory `items`
      // array is replaced with the server's saved row. A direct
      // service call would PATCH the DB but leave the rendered card
      // bound to the stale `item` prop, making the save look like a
      // no-op until a full dashboard reload.
      await updateItem(item.id, parsed);
      antMessage.success('Component updated');
      setEditJsonModalOpen(false);
      setEditJsonError('');
      // Force the SQL result cache to refresh as well — the new
      // chart_config may reference different columns.
      setData(null);
      loadData(true);
    } catch (e) {
      setEditJsonError(e.message || 'Invalid JSON');
    }
  };

  // Naive but string-literal-safe SQL pretty-printer. Walks the input once
  // and only replaces top-level keywords; anything inside '...' / "..."
  // strings is passed through untouched. Previous version corrupted SQL
  // like `WHERE name = 'M AND M'`.
  const formatSql = (sql) => {
    if (!sql) return 'No SQL query';
    const KEYWORDS = [
      ['LEFT JOIN',  '\nLEFT JOIN'],
      ['RIGHT JOIN', '\nRIGHT JOIN'],
      ['INNER JOIN', '\nINNER JOIN'],
      ['JOIN',       '\nJOIN'],
      ['GROUP BY',   '\nGROUP BY'],
      ['ORDER BY',   '\nORDER BY'],
      ['FROM',       '\nFROM'],
      ['WHERE',      '\nWHERE'],
      ['LIMIT',      '\nLIMIT'],
      ['AND',        '\n  AND'],
      ['OR',         '\n  OR'],
      ['SELECT',     'SELECT'],
    ];
    const isWordChar = (c) => /[A-Za-z0-9_]/.test(c);
    let out = '';
    let i = 0;
    while (i < sql.length) {
      const c = sql[i];
      if (c === "'" || c === '"') {
        // copy whole string literal verbatim, handling doubled-quote escapes
        const quote = c;
        out += c; i++;
        while (i < sql.length) {
          if (sql[i] === quote && sql[i + 1] === quote) { out += quote + quote; i += 2; continue; }
          out += sql[i];
          if (sql[i] === quote) { i++; break; }
          i++;
        }
        continue;
      }
      // Try to match a keyword at a word boundary.
      let matched = false;
      const prev = i === 0 ? '' : sql[i - 1];
      if (!isWordChar(prev)) {
        const upper = sql.slice(i, i + 12).toUpperCase();
        for (const [kw, rep] of KEYWORDS) {
          if (upper.startsWith(kw)) {
            const next = sql[i + kw.length];
            if (next === undefined || !isWordChar(next)) {
              out += rep;
              i += kw.length;
              matched = true;
              break;
            }
          }
        }
      }
      if (!matched) { out += c; i++; }
    }
    return out;
  };

  const settingsMenuItems = [
    {
      key: 'refresh',
      label: 'Refresh',
      icon: <ReloadOutlined />,
      onClick: () => loadData(true),
      disabled: loading,
    },
    ...(canEdit && onEditItem ? [
      { type: 'divider' },
      {
        key: 'edit-chart',
        label: item.item_type === 'table' ? 'Edit table' : item.item_type === 'metric' ? 'Edit metric' : 'Edit in Chart Editor',
        icon: <BarChartOutlined />,
        onClick: () => onEditItem(item),
      },
      ...(onMoveToTab && availableTabs.length > 0 ? [{
        key: 'move-tab',
        label: 'Move to tab',
        icon: <FolderOpenOutlined />,
        children: availableTabs.map((tab) => ({
          key: `move-tab-${tab}`,
          label: tab,
          disabled: (item.tab_name || 'Overview') === tab,
          onClick: () => onMoveToTab(item, tab),
        })),
      }] : []),
    ] : []),
    { type: 'divider' },
    {
      key: 'sql',
      label: 'Show SQL Query',
      icon: <EyeOutlined />,
      onClick: () => setSqlModalOpen(true),
    },
    {
      key: 'data',
      label: 'Show Data',
      icon: <TableOutlined />,
      onClick: () => setDataModalOpen(true),
    },
    ...(item.item_type === 'table' && data ? [{
      key: 'csv',
      label: 'Download CSV',
      icon: <DownloadOutlined />,
      onClick: handleDownloadCSV,
    }] : []),
    ...(canEdit ? [
      { type: 'divider' },
      {
        key: 'developer',
        label: 'Developer',
        icon: <ExperimentOutlined />,
        children: [
          { key: 'json', label: 'Show JSON', icon: <CodeOutlined />, onClick: () => setJsonModalOpen(true) },
          { key: 'edit-json', label: 'Edit JSON', icon: <EditOutlined />, onClick: handleOpenEditJson },
        ],
      },
    ] : []),
  ];

  const focusClass = isFocused ? ' dashboard-card-focused dx-focus-ring' : '';
  return (
    <div className={`dashboard-card${focusClass}`} onClick={() => onFocus(item)}>
      {/* Header — acts as drag handle in edit mode */}
      <div className={`dashboard-card-header${canEdit ? ' drag-handle' : ''}`}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          {item.item_type === 'table' ? (
            <TableOutlined style={{ color: 'var(--theme-primary)', flexShrink: 0 }} />
          ) : item.item_type === 'metric' ? (
            <ExperimentOutlined style={{ color: 'var(--theme-primary)', flexShrink: 0 }} />
          ) : (
            <BarChartOutlined style={{ color: 'var(--theme-primary)', flexShrink: 0 }} />
          )}
          <Text ellipsis style={{ fontWeight: 600, fontSize: 14, margin: 0 }}>{item.title}</Text>
        </div>
        <div className="no-drag dashboard-card-actions-hover" style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
          {canEdit && onEditItem && (
            <Tooltip title={item.item_type === 'table' ? 'Edit table' : item.item_type === 'metric' ? 'Edit metric' : 'Edit in Chart Editor'}>
              <Button
                className="dashboard-card-actions-edit-only"
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={(e) => { e.stopPropagation(); onEditItem(item); }}
              />
            </Tooltip>
          )}
          <Tooltip title="Fullscreen">
            <Button type="text" size="small" icon={<FullscreenOutlined />} onClick={(e) => { e.stopPropagation(); setIsFullscreen(true); }} />
          </Tooltip>
          <Dropdown menu={{ items: settingsMenuItems }} trigger={['click']} placement="bottomRight">
            <Tooltip title="More">
              <Button type="text" size="small" icon={<MoreOutlined />} onClick={(e) => e.stopPropagation()} />
            </Tooltip>
          </Dropdown>
          {canEdit && (
            <Popconfirm title="Remove from dashboard?" onConfirm={() => onRemove(item.id)} okText="Remove" placement="left">
              <Tooltip title="Remove">
                <Button className="dashboard-card-actions-edit-only" type="text" size="small" danger icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          )}
        </div>
      </div>

      {/* WHERE filter bar — only shown when SQL has parseable WHERE conditions */}
      {Object.keys(parsedWhereFilters).length > 0 && (
        <ComponentFilterBar
          parsedFilters={parsedWhereFilters}
          filterValues={mergedComponentFilters}
          disabledFilters={mergedDisabledFilters}
          data={data}
          onFilterChange={handleComponentFilterChange}
          onToggleDisabled={handleToggleComponentFilterDisabled}
          onReset={handleResetComponentFilters}
        />
      )}

      {/* Body */}
      <div className="dashboard-card-body">
        {item.item_type === 'metric' ? (
          // MetricCard runs its own SQL & rendering — bypass the
          // shared loading/data path used by table/chart items.
          <MetricCard item={item} sqlOverride={effectiveSql !== item.sql_query ? effectiveSql : null} />
        ) : (
        <>
        {(loading || isStale) && <Skeleton kind={item.item_type === 'table' ? 'table' : 'chart'} />}
        {error && (
          <ErrorPanel
            message={error}
            onRetry={() => { setError(null); loadData(true); }}
            onShowSql={() => setSqlModalOpen(true)}
            onEditQuery={canEdit && onEditItem ? () => onEditItem(item) : undefined}
          />
        )}
        {!loading && !isStale && !error && data && item.item_type === 'table' && (
          filteredData.columns && filteredData.rows ? (
            <AdvancedTable data={filteredData} dataSummary={filteredData.data_summary} maxRows={200} fill />
          ) : (
            <div style={{ overflow: 'auto', height: '100%', fontSize: 12 }}>
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 11 }}>
                {JSON.stringify(data, null, 2)}
              </pre>
            </div>
          )
        )}
        {!loading && !isStale && !error && data && item.item_type === 'chart' && (
          isPlotlyFigure(item.chart_config) ? (
            <PlotlyChartCard item={item} isInteracting={isInteracting} sqlOverride={effectiveSql !== item.sql_query ? effectiveSql : null} />
          ) : (
            <FilterableChart chartSpec={item.chart_config} tableData={filteredData} fill />
          )
        )}
        {!loading && !isStale && !error && !data && (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />
        )}
        </>
        )}
      </div>

      {/* JSON Configuration Modal */}
      <Modal
        title="Component Configuration (JSON)"
        open={jsonModalOpen}
        onCancel={(e) => { e.stopPropagation(); setJsonModalOpen(false); }}
        width={700}
        footer={[
          <Button key="copy" type="primary" onClick={() => handleCopyToClipboard(getComponentJson(), 'Copied!')}>
            {copiedText === 'Copied!' ? '✓ Copied!' : 'Copy to Clipboard'}
          </Button>,
          <Button key="close" onClick={() => setJsonModalOpen(false)}>Close</Button>,
        ]}
        onClick={(e) => e.stopPropagation()}
      >
        <pre style={{
          backgroundColor: '#f5f5f5', padding: 16, borderRadius: 4,
          maxHeight: 500, overflow: 'auto',
          fontFamily: 'Consolas, Monaco, "Courier New", monospace',
          fontSize: 13, lineHeight: 1.5,
        }}>
          {getComponentJson()}
        </pre>
      </Modal>

      {/* Edit JSON Modal */}
      <Modal
        title="Edit Component JSON"
        open={editJsonModalOpen}
        onCancel={(e) => { e.stopPropagation(); setEditJsonModalOpen(false); setEditJsonError(''); }}
        width={700}
        footer={[
          <Button key="save" type="primary" onClick={handleSaveEditJson}>Save</Button>,
          <Button key="cancel" onClick={() => { setEditJsonModalOpen(false); setEditJsonError(''); }}>Cancel</Button>,
        ]}
        onClick={(e) => e.stopPropagation()}
      >
        {editJsonError && <Alert type="error" message={editJsonError} style={{ marginBottom: 8 }} showIcon />}
        <Input.TextArea
          value={editJsonString}
          onChange={(e) => setEditJsonString(e.target.value)}
          rows={20}
          style={{ fontFamily: 'Consolas, Monaco, "Courier New", monospace', fontSize: 13 }}
        />
      </Modal>

      {/* SQL Query Modal — shows the original SQL, the active dashboard- and
          component-level filter overrides, and the executed SQL after those
          WHERE-clause overrides have been applied. */}
      <Modal
        title="🔍 SQL Query"
        open={sqlModalOpen}
        onCancel={(e) => { e.stopPropagation(); setSqlModalOpen(false); }}
        width={900}
        footer={[
          <Button key="copy" type="primary" onClick={() => handleCopyToClipboard(effectiveSql, 'SQL Copied!')}>
            {copiedText === 'SQL Copied!' ? '✓ Copied!' : 'Copy Executed SQL'}
          </Button>,
          <Button key="close" onClick={() => setSqlModalOpen(false)}>Close</Button>,
        ]}
        onClick={(e) => e.stopPropagation()}
      >
        {(() => {
          const codeBox = {
            backgroundColor: '#f0f8ff', padding: 12, borderRadius: 6,
            maxHeight: 240, overflow: 'auto',
            fontFamily: 'Monaco, Consolas, "Lucida Console", monospace',
            fontSize: 12, lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            border: '1px solid #b3d9ff', margin: 0,
          };
          // Build a single per-column row that shows the EFFECTIVE value
          // actually used in the executed SQL, plus where it came from
          // (Original SQL, Dashboard-level, or Component-level override).
          const filterRows = Object.entries(parsedWhereFilters).map(([col, def]) => {
            const effective = mergedComponentFilters[col] || def.values;
            const dashVal = dashboardFilters?.[col];
            const eqArr = (a, b) => JSON.stringify([...(a || [])].map(String).sort())
              === JSON.stringify([...(b || [])].map(String).sort());
            const isOriginal = eqArr(effective, def.values);
            const isDashboard = !isOriginal && dashVal && dashVal.length > 0 && eqArr(effective, dashVal);
            const source = isOriginal ? 'original' : (isDashboard ? 'dashboard' : 'component');
            return { col, type: def.type, original: def.values, effective, source };
          });
          const overriddenRows = filterRows.filter(r => r.source !== 'original');
          const noFilters = overriddenRows.length === 0;
          return (
            <>
              <div style={{ marginBottom: 16 }}>
                <Typography.Text strong style={{ display: 'block', marginBottom: 6, fontSize: 13 }}>
                  Original SQL Query
                </Typography.Text>
                <pre style={codeBox}>{formatSql(item.sql_query)}</pre>
              </div>
              <div style={{ marginBottom: 16 }}>
                <Typography.Text strong style={{ display: 'block', marginBottom: 6, fontSize: 13 }}>
                  Applied Filters
                </Typography.Text>
                {noFilters ? (
                  <div style={{ fontSize: 12, color: 'var(--theme-textSecondary)', fontStyle: 'italic' }}>
                    No active filters — executed SQL matches the original.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {overriddenRows.map(({ col, type, original, effective, source }) => {
                      const sourceTag = source === 'dashboard'
                        ? <Tag color="blue" style={{ marginRight: 6 }}>Dashboard-level</Tag>
                        : <Tag color="purple" style={{ marginRight: 6 }}>Component-level</Tag>;
                      return (
                        <div key={col} style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
                          {sourceTag}
                          <Tag style={{ margin: 0 }}>
                            <strong>{col}</strong> ({type}):{' '}
                            <span style={{ textDecoration: 'line-through', color: '#bfbfbf' }}>{original.join(', ')}</span>
                            {' → '}
                            <strong style={{ color: 'var(--theme-primary)' }}>{effective.join(', ')}</strong>
                          </Tag>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <div>
                <Typography.Text strong style={{ display: 'block', marginBottom: 6, fontSize: 13 }}>
                  Executed SQL Query
                </Typography.Text>
                <pre style={{ ...codeBox, backgroundColor: '#f6ffed', borderColor: '#b7eb8f', maxHeight: 280 }}>
                  {formatSql(effectiveSql)}
                </pre>
              </div>
            </>
          );
        })()}
      </Modal>

      {/* Data View Modal */}
      <Modal
        title={`📊 Data View — ${item.title}`}
        open={dataModalOpen}
        onCancel={(e) => { e.stopPropagation(); setDataModalOpen(false); }}
        width={1400}
        footer={[<Button key="close" onClick={() => setDataModalOpen(false)}>Close</Button>]}
        onClick={(e) => e.stopPropagation()}
      >
        {!data ? (
          <Empty description="No data loaded yet" />
        ) : (
          <Tabs
            defaultActiveKey="table"
            items={[
              {
                key: 'table',
                label: <span>Data <Tag color="blue" style={{ marginLeft: 8 }}>{data.row_count ?? (data.rows || []).length} rows</Tag></span>,
                children: (
                  <div>
                    <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography.Text type="secondary">
                        Columns: {(data.columns || []).join(', ')}
                      </Typography.Text>
                      {item.item_type === 'table' && (
                        <Button type="primary" size="small" icon={<DownloadOutlined />} onClick={handleDownloadCSV}>
                          Download CSV
                        </Button>
                      )}
                    </div>
                    <Table
                      dataSource={(data.rows || []).map((row, i) => {
                        const obj = { __key: i };
                        (data.columns || []).forEach((col, ci) => { obj[col] = Array.isArray(row) ? row[ci] : row[col]; });
                        return obj;
                      })}
                      columns={(data.columns || []).map(col => ({
                        title: col, dataIndex: col, key: col, width: 150,
                        render: (val) => {
                          if (val === null || val === undefined) return <span style={{ color: '#999', fontStyle: 'italic' }}>null</span>;
                          if (typeof val === 'object') return <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{JSON.stringify(val)}</span>;
                          if (typeof val === 'boolean') return <Tag color={val ? 'green' : 'red'}>{String(val)}</Tag>;
                          if (typeof val === 'number') return <span style={{ fontFamily: 'monospace' }}>{val.toLocaleString()}</span>;
                          return String(val);
                        },
                      }))}
                      rowKey="__key"
                      scroll={{ x: 'max-content', y: 500 }}
                      pagination={{ pageSize: 50, showSizeChanger: true, showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} rows` }}
                      size="small"
                      bordered
                    />
                  </div>
                ),
              },
              {
                key: 'raw',
                label: 'Raw JSON',
                children: (
                  <pre style={{
                    backgroundColor: '#f5f5f5', padding: 16, borderRadius: 4,
                    maxHeight: 500, overflow: 'auto',
                    fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                    fontSize: 12, lineHeight: 1.5,
                  }}>
                    {JSON.stringify(data, null, 2)}
                  </pre>
                ),
              },
            ]}
          />
        )}
      </Modal>

      {/* Fullscreen Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {item.item_type === 'table' ? <TableOutlined style={{ color: '#52c41a' }} /> : <BarChartOutlined style={{ color: '#fa8c16' }} />}
            {item.title}
          </div>
        }
        open={isFullscreen}
        onCancel={(e) => { e.stopPropagation(); setIsFullscreen(false); }}
        footer={null}
        width="95vw"
        centered
        styles={{ body: { height: '80vh', overflow: 'hidden', padding: 0 } }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="dashboard-fullscreen-body" style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* WHERE filter bar in fullscreen too */}
          {Object.keys(parsedWhereFilters).length > 0 && (
            <ComponentFilterBar
              parsedFilters={parsedWhereFilters}
              filterValues={mergedComponentFilters}
              disabledFilters={mergedDisabledFilters}
              data={data}
              onFilterChange={handleComponentFilterChange}
              onToggleDisabled={handleToggleComponentFilterDisabled}
              onReset={handleResetComponentFilters}
            />
          )}
          <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            {loading && <Skeleton kind={item.item_type === 'table' ? 'table' : 'chart'} />}
            {error && (
              <ErrorPanel
                message={error}
                onRetry={() => { setError(null); loadData(true); }}
                onShowSql={() => setSqlModalOpen(true)}
              />
            )}
            {!loading && !error && data && item.item_type === 'table' && (
              filteredData?.columns && filteredData?.rows ? (
                <AdvancedTable data={filteredData} dataSummary={filteredData.data_summary} maxRows={500} fill />
              ) : (
                <pre style={{ margin: 0, padding: 16, whiteSpace: 'pre-wrap', fontSize: 12 }}>
                  {JSON.stringify(data, null, 2)}
                </pre>
              )
            )}
            {!loading && !error && data && item.item_type === 'chart' && (
              <FilterableChart chartSpec={item.chart_config} tableData={filteredData} fill />
            )}
            {!loading && !error && !data && <Empty description="No data" />}
          </div>
        </div>
      </Modal>
    </div>
  );
}

/**
 * Simple wrapper for dashboard cards in the grid.
 * Data loading is handled inside DashboardCard (cache-first, loads on mount).
 */
function ObservedCard({ item, onRemove, isFocused, onFocus, onEditItem, onMoveToTab, availableTabs, canEdit, isInteracting }) {
  return (
    <div style={{ height: '100%' }}>
      <DashboardCard
        item={item}
        onRemove={onRemove}
        isVisible={true}
        isFocused={isFocused}
        onFocus={onFocus}
        onEditItem={onEditItem}
        onMoveToTab={onMoveToTab}
        availableTabs={availableTabs}
        canEdit={canEdit}
        isInteracting={isInteracting}
      />
    </div>
  );
}

/**
 * Dashboard navigation bar — single-active-dashboard layout (no tabs).
 *
 * Like a professional BI tool (Tableau, Power BI, Looker), only one
 * dashboard is open at a time. Users switch via a dropdown picker
 * anchored to the active dashboard's title. Right-click on the title
 * opens the same rename / set-default / share / delete context menu
 * that previously lived on tabs.
 */
function DashboardTabBar({ onGoHome, onShare, isHomePage, onSwitchTab }) {
  const { dashboards, activeDashboardId, renameDashboard, removeDashboard, setDefaultDashboard, updateDashboardMetadata, draftDashboard } = useDashboard();
  const [renameModal, setRenameModal] = useState({ visible: false, id: null, name: '', description: '' });
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');

  const handleRename = async () => {
    if (!newName.trim()) return;
    try {
      if (renameModal.id === DRAFT_DASHBOARD_ID) {
        await renameDashboard(renameModal.id, newName.trim());
      } else {
        await updateDashboardMetadata(renameModal.id, {
          name: newName.trim(),
          description: newDescription.trim() || null,
        });
      }
      setRenameModal({ visible: false, id: null, name: '', description: '' });
    } catch (err) {
      antMessage.error('Failed to rename dashboard');
    }
  };

  const handleDelete = async (dashId) => {
    try {
      await removeDashboard(dashId);
    } catch (err) {
      antMessage.error('Failed to delete dashboard');
    }
  };

  const handleSetDefault = async (dashId) => {
    try {
      await setDefaultDashboard(dashId);
      antMessage.success('Default dashboard updated');
    } catch (err) {
      antMessage.error('Failed to set default');
    }
  };

  // Active item — either the draft (if it is the current selection)
  // or a real dashboard from the list.
  const isDraftActive = !!draftDashboard && activeDashboardId === draftDashboard.id;
  const activeDash = isDraftActive
    ? { id: draftDashboard.id, name: draftDashboard.name, item_count: 0, is_default: false, isDraft: true }
    : dashboards.find(d => d.id === activeDashboardId);

  // Switcher dropdown — every persisted dashboard, plus the draft when present.
  const switcherItems = [
    ...dashboards.map(d => ({
      key: String(d.id),
      label: (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {d.is_default && <StarFilled style={{ fontSize: 10, color: '#faad14' }} />}
          <span>{d.name}</span>
          <span style={{ fontSize: 10, opacity: 0.6 }}>({d.item_count})</span>
        </span>
      ),
      onClick: () => onSwitchTab(d.id),
    })),
    ...(draftDashboard ? [
      { type: 'divider' },
      {
        key: String(draftDashboard.id),
        label: (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontStyle: 'italic' }}>
            <span>{draftDashboard.name}</span>
            <span className="dashboard-tab-unsaved">Unsaved</span>
          </span>
        ),
        onClick: () => onSwitchTab(draftDashboard.id),
      },
    ] : []),
  ];

  // Context menu items for the active dashboard title (right-click).
  const titleContextItems = activeDash
    ? (activeDash.isDraft
        ? [
            { key: 'rename', icon: <EditOutlined />, label: 'Rename', onClick: () => { setRenameModal({ visible: true, id: activeDash.id, name: activeDash.name, description: activeDash.description || '' }); setNewName(activeDash.name); setNewDescription(activeDash.description || ''); } },
            { type: 'divider' },
            { key: 'discard', icon: <DeleteOutlined />, label: 'Discard', danger: true, onClick: () => handleDelete(activeDash.id) },
          ]
        : [
            { key: 'rename', icon: <EditOutlined />, label: 'Rename', onClick: () => { setRenameModal({ visible: true, id: activeDash.id, name: activeDash.name, description: activeDash.description || '' }); setNewName(activeDash.name); setNewDescription(activeDash.description || ''); } },
            { key: 'default', icon: activeDash.is_default ? <StarFilled /> : <StarOutlined />, label: activeDash.is_default ? 'Default ✓' : 'Set as Default', disabled: activeDash.is_default, onClick: () => handleSetDefault(activeDash.id) },
            { key: 'share', icon: <ShareAltOutlined />, label: 'Share', onClick: () => onShare && onShare(activeDash) },
            { type: 'divider' },
            { key: 'delete', icon: <DeleteOutlined />, label: 'Delete', danger: true, onClick: () => handleDelete(activeDash.id) },
          ])
    : [];

  return (
    <>
      <div className="dashboard-tab-bar">
        <Tooltip title="Dashboard Home">
          <Button
            type="text"
            size="small"
            icon={<HomeOutlined />}
            onClick={onGoHome}
            className={`dashboard-home-btn${isHomePage ? ' dashboard-home-btn-active' : ''}`}
          />
        </Tooltip>
        <span className="dashboard-tab-separator">|</span>
        {/* Active dashboard title (plain text). Right-click for actions.
            The dashboard switcher is rendered as a SEPARATE adjacent
            button so the title itself is not click-attached to a list. */}
        {!isHomePage && activeDash && (
          <>
            <Dropdown trigger={['contextMenu']} menu={{ items: titleContextItems }}>
              <div
                className={`dashboard-active-title-plain${activeDash.isDraft ? ' is-draft' : ''}`}
                title={activeDash.isDraft ? 'Unsaved draft — will be saved on first action' : activeDash.name}
              >
                {activeDash.is_default && <StarFilled style={{ fontSize: 12, color: '#faad14', marginRight: 6 }} />}
                <span className="dashboard-active-title-name">{activeDash.name}</span>
                {!activeDash.isDraft && typeof activeDash.item_count === 'number' && (
                  <span className="dashboard-tab-count">{activeDash.item_count}</span>
                )}
                {activeDash.isDraft && <span className="dashboard-tab-unsaved">Unsaved</span>}
              </div>
            </Dropdown>
            <Dropdown
              trigger={['click']}
              menu={{ items: switcherItems, selectable: true, selectedKeys: [String(activeDash.id)] }}
              placement="bottomLeft"
            >
              <Tooltip title="Switch dashboard">
                <Button
                  type="text"
                  size="small"
                  icon={<DownOutlined style={{ fontSize: 10 }} />}
                  className="dashboard-switcher-btn"
                  aria-label="Switch dashboard"
                />
              </Tooltip>
            </Dropdown>
          </>
        )}
        {/* Filler: pushes any future trailing controls to the right when no active title is shown (homepage). */}
        <span style={{ flex: 1 }} />
      </div>

      <Modal
        title="Edit Dashboard"
        open={renameModal.visible}
        onCancel={() => setRenameModal({ visible: false, id: null, name: '', description: '' })}
        onOk={handleRename}
        okText="Save"
        width={400}
      >
        <Input
          value={newName}
          onChange={e => setNewName(e.target.value)}
          onPressEnter={handleRename}
          maxLength={50}
          placeholder="Dashboard name"
          autoFocus
        />
        <Input.TextArea
          value={newDescription}
          onChange={(e) => setNewDescription(e.target.value)}
          rows={3}
          maxLength={500}
          placeholder="Dashboard description (optional)"
          style={{ marginTop: 12 }}
        />
      </Modal>
    </>
  );
}

/**
 * DashboardPanel — the sliding left-panel that hosts the grid of dashboard cards.
 * Now supports a homepage view + tabbed dashboard navigation + library + sharing.
 */
export default function DashboardPanel() {
  const { isAuthenticated } = useAuth();
  const { items, isOpen, isFullscreen, loading, closeDashboard, openDashboard, removeItem, updateItem, updateLayout, focusedComponent, focusComponent, clearFocusedComponent, dashboards, toggleFullscreen, setFullscreen, switchDashboard, activeDashboardId, openDashboardByUuid, density, setDensity, viewMode, setViewMode, requestShowDashboard, draftDashboard, createDraftDashboard, discardDraft, updateDashboardMetadata, setActiveDashboardTab, getCachedData } = useDashboard();
  const isEditMode = viewMode === 'edit';
  const activeDashboard = dashboards.find((d) => d.id === activeDashboardId);
  // Owners + admins can switch to Edit; everyone else stays in View.
  const canEditCurrent = !activeDashboard || activeDashboard.user_permission === 'owner'
    || activeDashboard.user_permission === 'edit' || activeDashboard.user_permission === undefined;
  const editEnabled = isEditMode && canEditCurrent;
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // ?mode=chat|hybrid|dashboard — a single, shareable URL knob that
  // captures which of the three layout modes the user is in:
  //   chat       — chat-only (panel closed)
  //   hybrid     — chat + dashboard side-by-side
  //   dashboard  — dashboard fullscreen with chat as a floating window
  const urlMode = searchParams.get('mode');
  const currentMode = !isOpen ? 'chat' : (isFullscreen ? 'dashboard' : 'hybrid');

  // URL → state: when the URL carries a mode that disagrees with the
  // current state, drive the dashboard context to match. Runs only on
  // url-mode changes so user-initiated state changes (toggle buttons)
  // are not fought.
  //
  // We use the idempotent `setFullscreen(bool)` setter (not the
  // non-idempotent `toggleFullscreen()`) here because React 18
  // StrictMode double-invokes effects in dev — calling toggle twice
  // with the same closure would cancel itself out, leaving the panel
  // stuck in hybrid layout despite ?mode=dashboard in the URL.
  //
  // Dep on `location.pathname` too: callers like `createAndOpenDashboard`
  // set `isFullscreen=false` (forcing currentMode='hybrid') BEFORE
  // their `navigate('/dashboard/<uuid>?mode=dashboard')` runs. When the
  // urlMode value didn't change (it was already 'dashboard' on the
  // previous /dashboards page), this effect would skip — leaving the
  // user on a `?mode=dashboard` URL with a hybrid layout. Re-running
  // on path changes catches that drift.
  // While a URL→state transition is in flight (set below) the state→URL
  // effect must not echo intermediate (isOpen, isFullscreen) values
  // back into the URL. Cleared once state has fully converged to
  // urlMode. Survives React 18 StrictMode (a ref, not state).
  const urlDrivenRef = useRef(false);
  useEffect(() => {
    if (!urlMode) return;
    if (urlMode === currentMode) return;
    // Mark this state mutation as URL-driven so the state→URL effect
    // skips intermediate writes while the two setters below settle
    // (they may not batch into the same render when other handlers
    // — e.g. CREATE DASHBOARD's createDraftDashboard / switchDashboard
    // — trigger interleaved renders).
    urlDrivenRef.current = true;
    if (urlMode === 'chat') {
      setFullscreen(false);
      closeDashboard();
    } else if (urlMode === 'hybrid') {
      if (!isOpen) openDashboard();
      setFullscreen(false);
    } else if (urlMode === 'dashboard') {
      if (!isOpen) openDashboard();
      setFullscreen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlMode, location.pathname]);

  // State → URL: keep ?mode= in sync with the dashboard context state
  // so the URL stays shareable / refresh-safe.
  //
  // This effect MUST only fire on real state transitions (user clicks
  // the close/fullscreen button, etc.) — never on the initial mount,
  // because at mount time the URL is the source of truth and the
  // URL→state effect above is what should drive panel state. Tracking
  // the previous values of isOpen/isFullscreen via refs gives us a
  // reliable "did the user actually change something?" signal that
  // also survives React 18 StrictMode's double-invoked effects (which
  // defeat a naive `firstRun` ref).
  const prevIsOpenRef = useRef(isOpen);
  const prevIsFullscreenRef = useRef(isFullscreen);
  useEffect(() => {
    const changed = prevIsOpenRef.current !== isOpen
      || prevIsFullscreenRef.current !== isFullscreen;
    prevIsOpenRef.current = isOpen;
    prevIsFullscreenRef.current = isFullscreen;
    if (!changed) return;
    // While a URL→state transition is in flight, swallow intermediate
    // state writes (e.g. (open=true, fullscreen=false) on the way to
    // dashboard mode would otherwise rewrite ?mode=dashboard back to
    // ?mode=hybrid). Clear the flag once state matches urlMode.
    if (urlDrivenRef.current) {
      if (!urlMode || urlMode === currentMode) urlDrivenRef.current = false;
      return;
    }
    const urlModeNow = searchParams.get('mode');
    if (urlModeNow === currentMode) return;
    // Genuine user-initiated state change with a stale URL — push
    // the new mode into the URL so it stays shareable.
    const next = new URLSearchParams(searchParams);
    next.set('mode', currentMode);
    setSearchParams(next, { replace: true });
  }, [isOpen, isFullscreen, currentMode, urlMode, searchParams, setSearchParams]);

  // Edit-session buffers — declared early so the dashboard-switch /
  // tab-close / home handlers can prompt to discard. Their wiring
  // (effects, computed flags, save/cancel handlers) lives further down.
  const [pendingLayout, setPendingLayout] = useState(null);
  const [pendingTrash, setPendingTrash] = useState([]);
  const [savingLayout, setSavingLayout] = useState(false);
  // True while the user is actively dragging or resizing a card. Used
  // to suppress expensive child renders (Plotly redraws once per
  // animation frame as RGL transitions sibling sizes during compaction
  // — the cumulative cost is what makes drag feel laggy on
  // chart-heavy dashboards).
  const [isInteracting, setIsInteracting] = useState(false);
  const hasDraftEdits = !!pendingLayout || pendingTrash.length > 0;
  const draftChangeCount = (pendingLayout ? 1 : 0) + pendingTrash.length;
  const confirmDiscardDraft = useCallback((proceed) => {
    if (!hasDraftEdits) { proceed(); return; }
    Modal.confirm({
      title: 'Discard changes?',
      content: `You have ${draftChangeCount} unsaved change${draftChangeCount === 1 ? '' : 's'}. Continuing will discard ${draftChangeCount === 1 ? 'it' : 'them'}.`,
      okText: 'Discard',
      okButtonProps: { danger: true },
      cancelText: 'Stay',
      onOk: () => {
        setPendingLayout(null);
        setPendingTrash([]);
        proceed();
      },
    });
  }, [hasDraftEdits, draftChangeCount]);

  // Chart editor (slide-in) state — drives the "+ New item" button and
  // the per-card "Edit in Chart Editor" action.
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editorInitialType, setEditorInitialType] = useState(null);
  const openEditor = useCallback((item = null, opts = {}) => {
    setEditingItem(item);
    setEditorInitialType(opts.initialItemType || null);
    setEditorOpen(true);
  }, []);
  const closeEditor = useCallback(() => {
    setEditorOpen(false);
    setEditingItem(null);
    setEditorInitialType(null);
  }, []);
  // Extract dashboard UUID from URL: /data/chat/dashboard/:uuid
  const urlUuidMatch = location.pathname.match(/^\/data\/chat\/dashboard\/([^/]+)$/);
  const dashboardUuid = urlUuidMatch ? urlUuidMatch[1] : null;
  // The homepage has its own canonical URL: /data/chat/dashboards
  const isHomepageUrl = location.pathname === '/data/chat/dashboards';
  // /data/chat (no /dashboards, no uuid) is the chat-only canonical
  // URL — the dashboard panel must NOT auto-show the homepage view
  // here, otherwise the URL-sync effect below would bounce the user
  // to /data/chat/dashboards on every visit to plain /data/chat.
  const isChatOnlyUrl = location.pathname === '/data/chat';
  const [zoomPercent, setZoomPercent] = useState(100);
  const contentRef = useRef(null);
  const [measuredWidth, setMeasuredWidth] = useState(0);
  const [showHomepage, setShowHomepage] = useState(!dashboardUuid && !isChatOnlyUrl);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [shareDialog, setShareDialog] = useState({ visible: false, dashboard: null });
  const [activeContentTab, setActiveContentTab] = useState('Overview');
  const [createTabModalOpen, setCreateTabModalOpen] = useState(false);
  const [newTabName, setNewTabName] = useState('');
  const [renameTabModalOpen, setRenameTabModalOpen] = useState(false);
  const [renameTabOldName, setRenameTabOldName] = useState('');
  const [renameTabNewName, setRenameTabNewName] = useState('');
  const lastActiveTabByDashboardRef = useRef(runtimeLastActiveTabByDashboard);
  const openedDashboardOnceRef = useRef(runtimeOpenedDashboardOnce);
  const pendingLoadedDashboardRef = useRef(null);
  const prevDashboardForTabRef = useRef(activeDashboardId);

  // Reset tab memory on logout so next login starts from default-tab first-open.
  useEffect(() => {
    if (!isAuthenticated) {
      lastActiveTabByDashboardRef.current = {};
      openedDashboardOnceRef.current = {};
      Object.keys(runtimeLastActiveTabByDashboard).forEach((k) => delete runtimeLastActiveTabByDashboard[k]);
      Object.keys(runtimeOpenedDashboardOnce).forEach((k) => delete runtimeOpenedDashboardOnce[k]);
    }
  }, [isAuthenticated]);

  // Dashboard-level developer tools
  // 1) Read-only "Dashboard state as JSON" — full snapshot intended for
  //    agent consumption: combines item metadata (SQL, chart_config,
  //    layout) with the actual rows returned by each item's SQL.
  const [dashStateModalOpen, setDashStateModalOpen] = useState(false);
  const [dashStateString, setDashStateString] = useState('');
  const [dashStateLoading, setDashStateLoading] = useState(false);
  const [dashStateCopied, setDashStateCopied] = useState(false);

  // 2) View & Edit dashboard metadata — the full editable export payload
  //    (name, description, items with SQL/config/layout). Used to copy,
  //    clone, or move a dashboard between environments via clipboard.
  const [dashMetaModalOpen, setDashMetaModalOpen] = useState(false);
  const [dashMetaString, setDashMetaString] = useState('');
  const [dashMetaOriginal, setDashMetaOriginal] = useState('');
  const [dashMetaEditing, setDashMetaEditing] = useState(false);
  const [dashMetaError, setDashMetaError] = useState('');
  const [dashMetaSaving, setDashMetaSaving] = useState(false);
  const [dashMetaCopied, setDashMetaCopied] = useState(false);

  // Single-active-dashboard model: only one dashboard is open at a time
  // (like Tableau / Power BI / Looker). No tab list to maintain — the
  // active dashboard is whatever activeDashboardId points at, and the
  // user switches via the title-anchored switcher dropdown or the
  // dashboard homepage.

  // When the chat asks the panel to focus a specific dashboard (via
  // add-to-dashboard from chat), drop the homepage view so the user
  // lands on the actual dashboard with their newly-added card.
  useEffect(() => {
    if (requestShowDashboard > 0 && activeDashboardId) {
      setShowHomepage(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestShowDashboard]);

  // /data/chat/dashboards is the canonical URL for the dashboard
  // homepage (so it can be linked, refreshed, or bookmarked). When the
  // URL is on the homepage, force showHomepage=true and auto-open the
  // panel so a direct visit lands on the homepage view — UNLESS the
  // URL explicitly requests `?mode=chat`, which means the user wants
  // chat-only and the panel must stay closed (otherwise this effect
  // and the ?mode= sync below fight each other and the URL flips
  // between chat ↔ hybrid forever).
  useEffect(() => {
    if (isHomepageUrl) {
      // Don't force the homepage list when a draft is active — the
      // sidebar CREATE DASHBOARD flow puts the user on /dashboards as
      // the canonical "draft host" URL but expects the empty draft
      // surface, not the dashboards grid.
      if (!showHomepage && activeDashboardId !== DRAFT_DASHBOARD_ID) setShowHomepage(true);
      if (urlMode !== 'chat') openDashboard();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHomepageUrl]);

  // Whenever the URL carries a specific dashboard uuid, the homepage
  // view must be hidden — otherwise the URL-sync effect below sees
  // `showHomepage === true` (e.g. from a previous visit to
  // /data/chat/dashboards) and rewrites the URL back to the homepage
  // path, swallowing the user's click.
  useEffect(() => {
    if (dashboardUuid && showHomepage) setShowHomepage(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardUuid]);

  // When a draft becomes active (e.g. user clicked sidebar
  // CREATE DASHBOARD from chat), hide the homepage list so the panel
  // renders the empty draft surface instead of the dashboards grid.
  // Drafts have no UUID so we can't drive this via the URL — the
  // /data/chat/dashboards path doubles as the draft host.
  //
  // Conversely, when the active draft is cleared (e.g. user clicked
  // LIST DASHBOARDS while the draft surface was up), the URL is
  // already /data/chat/dashboards so the URL→state effect won't fire
  // — bring back the homepage grid here so the click visibly works.
  useEffect(() => {
    if (activeDashboardId === DRAFT_DASHBOARD_ID && showHomepage) {
      setShowHomepage(false);
    } else if (isHomepageUrl && activeDashboardId !== DRAFT_DASHBOARD_ID && !showHomepage) {
      setShowHomepage(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDashboardId]);

  // Helper: determine which tab to select for a dashboard
  // Priority: remembered (session) > default_tab (persisted) > first tab
  const buildTabsFromDashboardPayload = useCallback((fullDashboard) => {
    const fromMeta = Array.isArray(fullDashboard?.tabs) ? fullDashboard.tabs : [];
    const fromItems = Array.isArray(fullDashboard?.items)
      ? fullDashboard.items.map((it) => it?.tab_name)
      : [];
    return normalizeTabs([...fromMeta, ...fromItems]);
  }, []);

  const getNextTabToSelect = useCallback((dashboardId, fullDashboard, dashboardTabsList) => {
    const dashKey = String(dashboardId || '');
    const openedBefore = !!(openedDashboardOnceRef.current[dashKey] || runtimeOpenedDashboardOnce[dashKey]);
    openedDashboardOnceRef.current[dashKey] = true;
    runtimeOpenedDashboardOnce[dashKey] = true;

    // First open in this browser session: honor persisted default tab.
    if (!openedBefore) {
      if (fullDashboard?.default_tab && dashboardTabsList.includes(fullDashboard.default_tab)) {
        return fullDashboard.default_tab;
      }
      return dashboardTabsList[0] || 'Overview';
    }

    const remembered = getRememberedDashboardTab(lastActiveTabByDashboardRef, dashboardId);
    
    // Validate remembered tab still exists
    const validRemembered = remembered && dashboardTabsList.includes(remembered);
    if (validRemembered) return remembered;
    
    // Fall back to default tab
    if (fullDashboard?.default_tab && dashboardTabsList.includes(fullDashboard.default_tab)) {
      return fullDashboard.default_tab;
    }
    
    // Fall back to first available tab
    return dashboardTabsList[0] || 'Overview';
  }, []);

  // Open dashboard from URL UUID
  const handledUuid = useRef(null);
  // While we resolve the URL uuid → dashboard id → items, the items
  // array is still empty but we shouldn't render the "No items yet"
  // empty state — it confuses users who just refreshed a dashboard
  // that actually has charts. Track a local resolving flag.
  const [urlResolving, setUrlResolving] = useState(!!dashboardUuid);
  useEffect(() => {
    if (!dashboardUuid || handledUuid.current === dashboardUuid) return;
    handledUuid.current = dashboardUuid;
    setUrlResolving(true);
    if (urlMode !== 'chat') openDashboard(); // Ensure panel is open (unless chat-only mode requested)
    (async () => {
      try {
        if (activeDashboardId != null && activeDashboardId !== DRAFT_DASHBOARD_ID && activeContentTab) {
          rememberDashboardTab(lastActiveTabByDashboardRef, activeDashboardId, activeContentTab);
        }
        const dashId = await openDashboardByUuid(dashboardUuid);
        const wasSameDashboard = dashId === activeDashboardId;
        setShowHomepage(false);
        // URL-opened dashboards (e.g. someone shared a link) start in
        // View mode — same rule as `handleSelectDashboard`.
        setViewMode('view');
          const full = await switchDashboard(dashId);
          if (full && !wasSameDashboard) {
            pendingLoadedDashboardRef.current = { id: dashId, full };
          }
          if (full && wasSameDashboard) {
            const tabs = buildTabsFromDashboardPayload(full);
            const nextTab = getNextTabToSelect(dashId, full, tabs);
            setActiveContentTab(nextTab);
          }
      } catch (err) {
        antMessage.error('Dashboard not found or access denied');
        navigate({ pathname: '/data/chat/dashboards', search: window.location.search }, { replace: true });
        setShowHomepage(true);
      } finally {
        setUrlResolving(false);
      }
    })();
  }, [dashboardUuid]);

  // Sync URL when the active dashboard changes from inside the panel
  // (in-panel switcher dropdown / "open in new tab" / draft commit).
  // The URL is the source of truth for navigation entry — explicit
  // navigate() calls from menu handlers (DATA CHAT, LIST DASHBOARDS,
  // CREATE DASHBOARD, OPEN <name>) own the URL outright, and this
  // effect must NOT push them off-target.
  //
  // Past failure mode (the loop): when the user clicks a menu item
  // that calls `createAndOpenDashboard()` followed by `navigate(...)`,
  // the context updates `activeDashboardId` BEFORE navigate fires, so
  // path-sync runs once with stale `showHomepage=true` (from the old
  // /dashboards page) and pushes back to /dashboards. The
  // isHomepageUrl effect then re-asserts showHomepage=true, and the
  // two effects ping-pong forever.
  //
  // Hard rules to break the loop:
  //   1. Skip on the chat-only URL (/data/chat) — chat owns it.
  //   2. Skip on the homepage URL (/data/chat/dashboards) — explicit
  //      destination, never auto-leave or auto-rewrite.
  //   3. Only push from one /dashboard/<uuid> to another /dashboard/<uuid>
  //      (in-panel switcher case). Never push from /dashboards to
  //      /dashboard/<uuid>; that's the menu handler's job.
  //   4. Bail out unless `activeDashboardId` actually changed since
  //      the last run (StrictMode-safe).
  const prevActiveDashIdRef = useRef(activeDashboardId);
  useEffect(() => {
    const idChanged = prevActiveDashIdRef.current !== activeDashboardId;
    prevActiveDashIdRef.current = activeDashboardId;
    if (!idChanged) return;
    if (!activeDashboardId || activeDashboardId === DRAFT_DASHBOARD_ID) return;
    const path = window.location.pathname;
    // Sync the URL in two cases:
    //   a) We are already on a /data/chat/dashboard/<uuid> path and the
    //      user switched to a different dashboard from the in-panel
    //      switcher dropdown (replace the uuid).
    //   b) We are on the homepage path /data/chat/dashboards and the
    //      user clicked a dashboard row OR a draft just got committed
    //      (rename / add-from-chat). In this case `showHomepage` was
    //      already flipped to false by the caller, so syncing the URL
    //      to /dashboard/<uuid> reflects what the user actually sees.
    //      The isHomepageUrl effect only re-asserts showHomepage when
    //      the path is /dashboards, so navigating away from that path
    //      here is safe and does not trigger a ping-pong.
    const onUuidPath = path.startsWith('/data/chat/dashboard/');
    const onHomepagePath = path === '/data/chat/dashboards';
    if (!onUuidPath && !onHomepagePath) return;
    if (onHomepagePath && showHomepage) return; // user is on LIST view, not viewing a dashboard
    const dash = dashboards.find(d => d.id === activeDashboardId);
    if (!dash?.uuid) return;
    const targetPath = `/data/chat/dashboard/${dash.uuid}`;
    if (path === targetPath) return;
    const search = window.location.search;
    navigate({ pathname: targetPath, search }, { replace: true });
  }, [activeDashboardId, dashboards, navigate, showHomepage]);

  // Measure container width with ResizeObserver
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = entry.contentRect.width;
        if (w > 0) setMeasuredWidth(w);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Force a re-measure after fullscreen toggle (CSS transition takes ~300ms)
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const timer = setTimeout(() => {
      const w = el.getBoundingClientRect().width;
      if (w > 0) setMeasuredWidth(w);
    }, 350);
    return () => clearTimeout(timer);
  }, [isFullscreen]);

  // Exit fullscreen on Escape key
  useEffect(() => {
    if (!isFullscreen) return;
    const handleKey = (e) => { if (e.key === 'Escape') toggleFullscreen(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isFullscreen, toggleFullscreen]);

  const handleGoHome = () => {
    confirmDiscardDraft(() => {
      // Going home from the draft means the user abandoned the unsaved
      // dashboard — drop it so a stale "Untitled — Unsaved" tab doesn't
      // linger after they navigate elsewhere.
      if (activeDashboardId === DRAFT_DASHBOARD_ID) {
        discardDraft();
      }
      setShowHomepage(true);
      navigate({ pathname: '/data/chat/dashboards', search: window.location.search }, { replace: true });
    });
  };

  const handleSelectDashboard = async (dashboardId, dashHint) => {
    const wasSameDashboard = dashboardId === activeDashboardId;
    // Already on this dashboard? Skip the prompt — nothing structural
    // changes about the edit session.
    if (dashboardId === activeDashboardId && !showHomepage) {
      return;
    }
    confirmDiscardDraft(async () => {
      if (activeDashboardId != null && activeDashboardId !== DRAFT_DASHBOARD_ID && activeContentTab) {
        rememberDashboardTab(lastActiveTabByDashboardRef, activeDashboardId, activeContentTab);
      }
      // If we were sitting on the unsaved draft tab and the user is
      // navigating away, drop the draft. By construction it has no
      // committed content (a rename or addItem would have promoted it
      // to a real dashboard) so silent discard is safe.
      if (activeDashboardId === DRAFT_DASHBOARD_ID && dashboardId !== DRAFT_DASHBOARD_ID) {
        discardDraft();
      }
      setShowHomepage(false);
      // Existing dashboards always open in View mode — users have to opt
      // into Edit explicitly so accidental drags / clicks don't mutate
      // shared dashboards.
      setViewMode('view');
      // Drive the URL directly when we can resolve the uuid up-front.
      // This avoids a race with the `activeDashboardId`-driven effect
      // above that re-asserts `showHomepage=true` while the path is
      // still `/data/chat/dashboards`, which would otherwise prevent
      // the URL-sync effect below from leaving the homepage path.
      //
      // `dashboards` only contains the user's OWN dashboards. For shared
      // / global / template rows opened from the homepage, the row's
      // uuid is passed in via `dashHint` so we can still drive the URL
      // up-front and avoid the homepage bounce-back.
      const dash = dashboards.find(d => d.id === dashboardId) || dashHint;
      if (dash?.uuid) {
        const path = window.location.pathname;
        const targetPath = `/data/chat/dashboard/${dash.uuid}`;
        if (path !== targetPath) {
          navigate({ pathname: targetPath, search: window.location.search }, { replace: false });
        }
      }
      const full = await switchDashboard(dashboardId);
      if (full && !wasSameDashboard) {
        pendingLoadedDashboardRef.current = { id: dashboardId, full };
      }
      // When switching to a different dashboard, the dashboard-change
      // effect is the single source of truth for tab selection.
      // Only apply manual selection when we opened the currently-active
      // dashboard from homepage (no id change => effect won't run).
      if (wasSameDashboard) {
        const resolvedDashboard = full || activeDashboard;
        if (resolvedDashboard) {
          const tabs = full ? buildTabsFromDashboardPayload(full) : dashboardTabs;
          const nextTab = getNextTabToSelect(dashboardId, resolvedDashboard, tabs);
          setActiveContentTab(nextTab);
        }
      }
    });
  };

  // Called by the dashboard homepage list when a user deletes the
  // currently-active dashboard. Single-active-dashboard model: just
  // navigate back to the homepage if the active one was removed.
  const handleDashboardDeleted = (dashboardId) => {
    if (dashboardId === activeDashboardId) {
      setShowHomepage(true);
      navigate({ pathname: '/data/chat/dashboards', search: window.location.search }, { replace: true });
    }
  };

  const handleCreateDashboard = async () => {
    // Industrial standard ("draft" UX): no DB write yet. The dashboard
    // gets persisted on the first action that needs an id (rename, add
    // a card, etc.). If the user never does one of those, no orphan
    // empty dashboard ever lands in the DB.
    const draft = createDraftDashboard();
    setShowHomepage(false);
    await switchDashboard(draft.id);
    setViewMode('edit');
  };

  const dashboardTabs = useMemo(() => {
    const explicit = Array.isArray(activeDashboard?.tabs) ? activeDashboard.tabs : [];
    const fromItems = [...new Set((items || []).map((it) => String(it.tab_name || 'Overview').trim() || 'Overview'))];
    const merged = [];
    const seen = new Set();
    for (const tab of [...explicit, ...fromItems]) {
      const name = String(tab || '').trim();
      if (!name) continue;
      const key = name.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      merged.push(name);
    }
    return merged.length > 0 ? merged : ['Overview'];
  }, [activeDashboard?.tabs, items]);

  useEffect(() => {
    const prevDashboardId = prevDashboardForTabRef.current;
    const hasDashboardChanged = prevDashboardId !== activeDashboardId;

    if (hasDashboardChanged) {
      // While homepage is visible, dashboard changes can happen in the
      // background. Do not consume "first-open" semantics yet.
      if (showHomepage) {
        prevDashboardForTabRef.current = activeDashboardId;
        return;
      }

      if (prevDashboardId != null && prevDashboardId !== DRAFT_DASHBOARD_ID && activeContentTab) {
        rememberDashboardTab(lastActiveTabByDashboardRef, prevDashboardId, activeContentTab);
      }
      prevDashboardForTabRef.current = activeDashboardId;

      const pending = pendingLoadedDashboardRef.current;
      const hasPending = pending && String(pending.id) === String(activeDashboardId);
      const dashboardForSelection = hasPending ? pending.full : activeDashboard;
      const tabsForSelection = hasPending ? buildTabsFromDashboardPayload(pending.full) : dashboardTabs;
      const nextTab = getNextTabToSelect(activeDashboardId, dashboardForSelection, tabsForSelection);
      if (hasPending) {
        pendingLoadedDashboardRef.current = null;
      }

      if (nextTab !== activeContentTab) {
        setActiveContentTab(nextTab);
      }
      return;
    }

    if (!dashboardTabs.includes(activeContentTab)) {
      const remembered = getRememberedDashboardTab(lastActiveTabByDashboardRef, activeDashboardId);
      setActiveContentTab(
        remembered && dashboardTabs.includes(remembered)
          ? remembered
          : (dashboardTabs[0] || 'Overview')
      );
      return;
    }

    if (activeDashboardId != null && activeDashboardId !== DRAFT_DASHBOARD_ID && activeContentTab) {
      rememberDashboardTab(lastActiveTabByDashboardRef, activeDashboardId, activeContentTab);
    }
  }, [activeDashboardId, activeDashboard, dashboardTabs, activeContentTab, getNextTabToSelect, showHomepage, buildTabsFromDashboardPayload]);

  useEffect(() => {
    setActiveDashboardTab(activeContentTab || 'Overview');
  }, [activeContentTab, setActiveDashboardTab]);

  const handleRenameTab = useCallback(async () => {
    const oldName = renameTabOldName;
    const newName = (renameTabNewName || '').trim();
    if (!newName) return;
    if (!activeDashboardId || activeDashboardId === DRAFT_DASHBOARD_ID) {
      antMessage.error('Save dashboard first before renaming tabs.');
      return;
    }
    if (newName.toLowerCase() !== oldName.toLowerCase() &&
        dashboardTabs.some((t) => t.toLowerCase() === newName.toLowerCase())) {
      antMessage.error('Tab name already exists.');
      return;
    }
    try {
      await updateDashboardMetadata(activeDashboardId, { tab_renames: { [oldName]: newName } });
      await switchDashboard(activeDashboardId, { forceReload: true });
      if (activeContentTab === oldName) setActiveContentTab(newName);
      setRenameTabModalOpen(false);
      setRenameTabOldName('');
      setRenameTabNewName('');
      antMessage.success(`Tab renamed to "${newName}"`);
    } catch (err) {
      antMessage.error(err?.message || 'Failed to rename tab');
    }
  }, [activeDashboardId, activeContentTab, dashboardTabs, renameTabOldName, renameTabNewName, updateDashboardMetadata, switchDashboard]);

  const handleSetDefaultTab = useCallback(async (tabName) => {
    if (!activeDashboardId || activeDashboardId === DRAFT_DASHBOARD_ID) {
      antMessage.error('Save dashboard first.');
      return;
    }
    const activeDash = dashboards.find((d) => d.id === activeDashboardId);
    const currentDefault = activeDash?.default_tab;
    // Toggle off if already default
    const newDefault = currentDefault === tabName ? '' : tabName;
    try {
      await updateDashboardMetadata(activeDashboardId, { default_tab: newDefault });
      antMessage.success(newDefault ? `"${newDefault}" set as default tab` : 'Default tab cleared');
    } catch (err) {
      antMessage.error(err?.message || 'Failed to set default tab');
    }
  }, [activeDashboardId, dashboards, updateDashboardMetadata]);

  const handleCreateDashboardTab = useCallback(async () => {
    const name = (newTabName || '').trim();
    if (!name) return;
    if (!activeDashboardId || activeDashboardId === DRAFT_DASHBOARD_ID) {
      antMessage.error('Save dashboard first before creating tabs.');
      return;
    }
    if (dashboardTabs.some((t) => t.toLowerCase() === name.toLowerCase())) {
      antMessage.error('Tab name already exists.');
      return;
    }
    try {
      const nextTabs = [...dashboardTabs, name];
      await updateDashboardMetadata(activeDashboardId, { tabs: nextTabs });
      setActiveContentTab(name);
      setCreateTabModalOpen(false);
      setNewTabName('');
    } catch (err) {
      antMessage.error(err?.message || 'Failed to create tab');
    }
  }, [activeDashboardId, dashboardTabs, items, newTabName, updateDashboardMetadata]);

  const handleDeleteDashboardTab = useCallback((tabName) => {
    const name = String(tabName || '').trim();
    if (!name) return;
    if (!activeDashboardId || activeDashboardId === DRAFT_DASHBOARD_ID) {
      antMessage.error('Save dashboard first before deleting tabs.');
      return;
    }
    if (dashboardTabs.length <= 1) {
      antMessage.error('At least one tab is required.');
      return;
    }

    Modal.confirm({
      title: 'Delete Tab',
      content: `Are you sure you want to delete the "${name}" tab? All components inside it will be deleted.`,
      okText: 'Delete',
      okButtonProps: { danger: true },
      async onOk() {
        const remainingTabs = dashboardTabs.filter((t) => t !== name);
        const itemsInTab = (items || []).filter(
          (it) => String(it.tab_name || 'Overview').trim() === name
        );

        if (itemsInTab.length > 0) {
          const results = await Promise.allSettled(itemsInTab.map((it) => removeItem(it.id)));
          const failed = results.filter((r) => r.status === 'rejected').length;
          if (failed > 0) {
            antMessage.error(`Could not delete ${failed} component(s) in tab "${name}". Tab was not deleted.`);
            return;
          }
        }

        await updateDashboardMetadata(activeDashboardId, { tabs: remainingTabs });

        if (activeContentTab === name) {
          setActiveContentTab(remainingTabs[0] || 'Overview');
        }

        antMessage.success(`Tab "${name}" deleted`);
      },
    });
  }, [activeDashboardId, activeContentTab, dashboardTabs, items, removeItem, updateDashboardMetadata]);

  const handleMoveItemToTab = useCallback(async (item, tabName) => {
    try {
      await updateItem(item.id, { tab_name: tabName });
    } catch (err) {
      antMessage.error(err?.message || 'Failed to move item to tab');
    }
  }, [updateItem]);

  const handleShareDashboard = (dashboard) => {
    setShareDialog({ visible: true, dashboard });
  };

  const handleCloneDashboard = async () => {
    if (!activeDashboardId) return;
    try {
      const cloned = await cloneDashboard(activeDashboardId);
      antMessage.success(`Dashboard cloned as "${cloned.name}"`);
      await switchDashboard(cloned.id);
      setShowHomepage(false);
    } catch (err) {
      antMessage.error(err.message || 'Failed to clone dashboard');
    }
  };

  // ── Dashboard state as JSON ───────────────────────────────────────
  // Read-only snapshot intended to be pasted into a chat with an agent
  // so it can reason about the current dashboard. It combines item
  // metadata (SQL, chart_config, grid position) with the rows currently
  // rendered for each item. Cached results are reused; items without a
  // cache entry have their SQL executed on the fly (truncated to the
  // same 200-row limit the cards use).
  const handleShowDashboardState = async () => {
    if (!activeDashboardId || !activeDashboard) return;
    setDashStateLoading(true);
    setDashStateString('');
    setDashStateModalOpen(true);
    try {
      const snapshotItems = await Promise.all((items || []).map(async (it) => {
        const base = {
          id: it.id,
          item_type: it.item_type,
          title: it.title,
          sql_query: it.sql_query,
          chart_config: it.chart_config ?? null,
          layout: { x: it.grid_x, y: it.grid_y, w: it.grid_w, h: it.grid_h },
        };
        const cached = getCachedData ? getCachedData(it.id) : null;
        if (cached) return { ...base, row_count: Array.isArray(cached) ? cached.length : null, data: cached };
        if (!it.sql_query) return { ...base, row_count: 0, data: [], data_error: 'no SQL configured' };
        try {
          const res = await executeDashboardSQL(it.sql_query, 200, false);
          const rows = res?.data ?? [];
          return { ...base, row_count: Array.isArray(rows) ? rows.length : null, data: rows };
        } catch (err) {
          return { ...base, data: null, data_error: err?.message || 'query failed' };
        }
      }));
      const snapshot = {
        id: activeDashboard.id,
        uuid: activeDashboard.uuid,
        name: activeDashboard.name,
        description: activeDashboard.description ?? null,
        captured_at: new Date().toISOString(),
        item_count: snapshotItems.length,
        items: snapshotItems,
      };
      setDashStateString(JSON.stringify(snapshot, null, 2));
    } catch (err) {
      antMessage.error(err.message || 'Failed to build dashboard state');
      setDashStateModalOpen(false);
    } finally {
      setDashStateLoading(false);
    }
  };

  // ── Dashboard metadata view / edit ────────────────────────────────
  // The "metadata" payload is the full editable export of the dashboard
  // (name, description, items with SQL / chart_config / grid layout) \u2014
  // i.e. everything required to rebuild the same dashboard elsewhere.
  // Users can Copy the JSON, paste it on another environment, and Save
  // to overwrite an empty dashboard, effectively cloning across envs.
  const handleShowDashboardMetadata = async () => {
    if (!activeDashboardId) return;
    try {
      const json = await getDashboardJson(activeDashboardId);
      const text = JSON.stringify(json, null, 2);
      setDashMetaString(text);
      setDashMetaOriginal(text);
      setDashMetaEditing(false);
      setDashMetaError('');
      setDashMetaModalOpen(true);
    } catch (err) {
      antMessage.error(err.message || 'Failed to load dashboard metadata');
    }
  };

  const handleSaveDashboardMetadata = async () => {
    if (!activeDashboardId) return;
    let parsed;
    try {
      parsed = JSON.parse(dashMetaString);
    } catch (err) {
      setDashMetaError(err.message || 'Invalid JSON');
      return;
    }
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      setDashMetaError('Metadata must be a JSON object');
      return;
    }
    setDashMetaSaving(true);
    try {
      await updateDashboardFromJson(activeDashboardId, parsed);
      antMessage.success('Dashboard metadata saved');
      setDashMetaModalOpen(false);
      setDashMetaEditing(false);
      setDashMetaError('');
      await switchDashboard(activeDashboardId, { forceReload: true });
    } catch (err) {
      setDashMetaError(err.message || 'Failed to save metadata');
    } finally {
      setDashMetaSaving(false);
    }
  };

  // ── Edit session (Save / Cancel UX) ───────────────────────────────
  // In Edit mode, structural changes are buffered locally instead of
  // writing straight to the backend. The user explicitly commits via
  // Save (PATCHes the layout + DELETEs the trashed items) or discards
  // via Cancel (the grid snaps back, items reappear). View mode never
  // touches the pending state. (The buffer state is declared near the
  // top of the function so dashboard-switch / tab-close handlers can
  // prompt before discarding it.)
  //
  // Currently buffered:
  //   - drag / resize  →  pendingLayout
  //   - delete         →  pendingTrash (full item rows so we can restore)
  //
  // NOT buffered (each has its own explicit save UI):
  //   - add new component  →  Chart Editor's Save button
  //   - edit content / SQL →  Chart Editor's Save button

  // Items hidden because they're staged for deletion. Used by the grid
  // and the layout calculations so the dashboard reflects the user's
  // pending arrangement.
  const pendingDeleteIds = useMemo(
    () => new Set(pendingTrash.map((it) => it.id)),
    [pendingTrash],
  );
  const visibleItems = useMemo(
    () => items.filter((it) => !pendingDeleteIds.has(it.id)),
    [items, pendingDeleteIds],
  );
  const visibleItemsByTab = useMemo(() => {
    const key = activeContentTab || 'Overview';
    return visibleItems.filter((item) => (item.tab_name || 'Overview') === key);
  }, [visibleItems, activeContentTab]);

  // Reset pending state only when the dashboard itself changes — items
  // mutating within the same dashboard (agent edits, save commits, etc.)
  // should NOT silently wipe a user's unsaved edits.
  const prevDashboardIdRef = useRef(activeDashboardId);
  useEffect(() => {
    if (prevDashboardIdRef.current !== activeDashboardId) {
      prevDashboardIdRef.current = activeDashboardId;
      setPendingLayout(null);
      setPendingTrash([]);
    }
  }, [activeDashboardId]);

  // Leaving Edit mode any other way (mode toggle, lost edit permission)
  // also drops the draft.
  useEffect(() => {
    if (!editEnabled) {
      setPendingLayout(null);
      setPendingTrash([]);
    }
  }, [editEnabled]);

  // Reconcile the pending buffers with the latest `items` so stale ids
  // (e.g. an agent removed a chart while the user had it queued for
  // delete) don't poison Save. Skip while a save is in flight — the
  // commit handler holds the buffers in closures and we'd otherwise
  // race the loop.
  useEffect(() => {
    if (savingLayout) return;
    const liveIds = new Set(items.map((it) => String(it.id)));
    setPendingLayout((prev) => {
      if (!prev) return prev;
      const filtered = prev.filter((l) => liveIds.has(String(l.i)));
      if (filtered.length === prev.length) return prev;
      return filtered.length ? filtered : null;
    });
    setPendingTrash((prev) => {
      const filtered = prev.filter((it) => liveIds.has(String(it.id)));
      return filtered.length === prev.length ? prev : filtered;
    });
  }, [items, savingLayout]);

  // `static: true` per-item is the per-item lock react-grid-layout
  // honours unconditionally — `isDraggable={false}` at the Responsive
  // level wasn't always being picked up after a mode toggle, so we
  // belt-and-braces it here. View mode = static items.
  //
  // Metric tiles can shrink to 2×1 because the big-number layout still
  // reads well at that footprint. Charts/tables need 3×3 to avoid
  // rendering into an unusable sliver.
  const minSizeFor = (it) => (it?.item_type === 'metric' ? { minW: 2, minH: 1 } : { minW: 3, minH: 3 });
  const minByItemId = useMemo(() => {
    const m = new Map();
    visibleItemsByTab.forEach((it) => m.set(String(it.id), minSizeFor(it)));
    return m;
  }, [visibleItemsByTab]);
  const persistedLayout = visibleItemsByTab.map((item) => ({
    i: String(item.id),
    x: item.grid_x,
    y: item.grid_y,
    w: item.grid_w,
    h: item.grid_h,
    ...minSizeFor(item),
    static: !editEnabled,
  }));
  // While a draft is in flight, render the draft positions so the user
  // sees their pending arrangement before committing. Filter out
  // entries for trashed items so RGL doesn't try to place ghosts.
  const rawLayout = pendingLayout
    ? pendingLayout
        .filter((l) => !pendingDeleteIds.has(Number(l.i)) && !pendingDeleteIds.has(l.i))
        .map((l) => ({ ...l, ...(minByItemId.get(String(l.i)) || { minW: 3, minH: 3 }), static: !editEnabled }))
    : persistedLayout;

  // Collapse vertical whitespace in the rendered layout:
  //   1. Strip leading empty rows (every item at y >= N → shift up by N).
  //   2. In View mode, also collapse interior empty-row bands so a saved
  //      gap between rows (e.g. items at y=6 and y=18 with rows 12..17
  //      empty) doesn't render as a blank stripe across the dashboard.
  //      `compactType="horizontal"` only fills horizontal gaps; static
  //      items in View mode bypass RGL's compactor entirely, so without
  //      this pass the gap is rendered as-is. In Edit mode we leave
  //      interior gaps alone so the user can intentionally space items.
  // The shift is purely cosmetic for rendering; the persisted grid_y is
  // unchanged until the user saves a new layout.
  const layout = useMemo(() => {
    if (rawLayout.length === 0) return rawLayout;
    let out = rawLayout;
    const minY = out.reduce((m, l) => Math.min(m, l.y || 0), Infinity);
    if (Number.isFinite(minY) && minY > 0) {
      out = out.map((l) => ({ ...l, y: (l.y || 0) - minY }));
    }
    if (!editEnabled) {
      // Build row-occupancy bitmap then remap each unique y to a packed value.
      const occupied = new Set();
      let maxY = 0;
      out.forEach((l) => {
        const y0 = l.y || 0;
        const h = l.h || 1;
        for (let r = y0; r < y0 + h; r++) occupied.add(r);
        if (y0 + h > maxY) maxY = y0 + h;
      });
      const rowMap = new Map();
      let nextRow = 0;
      for (let r = 0; r < maxY; r++) {
        if (occupied.has(r)) {
          rowMap.set(r, nextRow);
          nextRow++;
        }
      }
      const needsRemap = out.some((l) => rowMap.get(l.y || 0) !== (l.y || 0));
      if (needsRemap) {
        out = out.map((l) => ({ ...l, y: rowMap.get(l.y || 0) ?? (l.y || 0) }));
      }
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(rawLayout), editEnabled]);

  const layoutDirty = useMemo(() => {
    if (!pendingLayout) return false;
    // Only compare against items that are still visible — pending
    // deletes are tracked separately.
    const byId = new Map(visibleItemsByTab.map((it) => [String(it.id), it]));
    const liveDraft = pendingLayout.filter((l) => byId.has(l.i));
    if (liveDraft.length !== visibleItemsByTab.length) return true;
    return liveDraft.some((l) => {
      const it = byId.get(l.i);
      return it.grid_x !== l.x || it.grid_y !== l.y || it.grid_w !== l.w || it.grid_h !== l.h;
    });
  }, [pendingLayout, visibleItemsByTab]);

  const trashDirty = pendingTrash.length > 0;
  const editDirty = layoutDirty || trashDirty;
  const pendingChangeCount = (layoutDirty ? 1 : 0) + pendingTrash.length;

  const handleDragOrResizeStop = useCallback((newLayout) => {
    setIsInteracting(false);
    if (!editEnabled) return;
    setPendingLayout(newLayout.map((l) => ({ i: l.i, x: l.x, y: l.y, w: l.w, h: l.h })));
  }, [editEnabled]);
  const handleDragOrResizeStart = useCallback(() => {
    setIsInteracting(true);
  }, []);

  // Wrapped delete: in Edit mode we buffer (so Cancel can restore the
  // item); in View mode (which shouldn't even surface the delete button)
  // we fall through to the immediate context call.
  const handleRemoveItem = useCallback((itemId) => {
    if (!editEnabled) {
      return removeItem(itemId);
    }
    const item = items.find((it) => it.id === itemId);
    if (!item) return undefined;
    setPendingTrash((prev) => (prev.some((t) => t.id === itemId) ? prev : [...prev, item]));
    clearFocusedComponent();
    return undefined;
  }, [editEnabled, items, removeItem, clearFocusedComponent]);

  const handleSaveLayout = useCallback(async () => {
    if (!editDirty) return;
    setSavingLayout(true);
    // Apply deletes first so the layout we then save reflects the
    // post-delete shape of the dashboard. Fire them in parallel — the
    // backend handles each independently and a partial failure here
    // doesn't leave anything inconsistent (committed deletes stay
    // committed, the user can retry the rest by hitting Save again).
    const deleteResults = await Promise.allSettled(
      pendingTrash.map((trashed) => removeItem(trashed.id)),
    );
    const deletedIds = new Set();
    const stillTrashed = [];
    pendingTrash.forEach((trashed, i) => {
      if (deleteResults[i].status === 'fulfilled') {
        deletedIds.add(String(trashed.id));
      } else {
        stillTrashed.push(trashed);
      }
    });
    const failures = deleteResults.filter((r) => r.status === 'rejected');

    let layoutFailure = null;
    if (pendingLayout && layoutDirty && failures.length === 0) {
      try {
        const cleanLayout = pendingLayout.filter(
          (l) => !deletedIds.has(String(l.i)) && !pendingDeleteIds.has(Number(l.i)) && !pendingDeleteIds.has(l.i),
        );
        if (cleanLayout.length) {
          await updateLayout(cleanLayout);
        }
      } catch (err) {
        layoutFailure = err;
      }
    }

    setPendingTrash(stillTrashed);
    if (failures.length === 0 && !layoutFailure) {
      setPendingLayout(null);
      antMessage.success('Changes saved');
    } else if (layoutFailure) {
      // Deletes succeeded but the layout PATCH failed — keep the layout
      // draft so the user can retry without re-arranging.
      antMessage.error(`Layout save failed: ${layoutFailure?.message || 'unknown error'}. Deletions committed.`);
    } else {
      // Some delete(s) failed; layout was skipped to avoid persisting an
      // arrangement that references items the backend still has.
      antMessage.error(`Failed to remove ${failures.length} item${failures.length === 1 ? '' : 's'}. ${stillTrashed.length} change${stillTrashed.length === 1 ? '' : 's'} still pending.`);
    }
    setSavingLayout(false);
  }, [editDirty, layoutDirty, pendingLayout, pendingTrash, pendingDeleteIds, removeItem, updateLayout]);

  const handleCancelLayout = useCallback(() => {
    setPendingLayout(null);
    setPendingTrash([]);
  }, []);

  if (!isOpen) {
    return null;
  }

  // Responsive column sizing — fill the panel's content area instead of
  // using a fixed pixel-per-column constant. The grid uses a 12-column
  // model and each column stretches to share the measured width evenly.
  // `measuredWidth` is set by a ResizeObserver on the content container,
  // so the grid re-flows when the panel is resized, fullscreened, or the
  // sidebar is toggled.
  const numCols = 12;
  const zoomScale = zoomPercent / 100;
  const baseGridWidth = measuredWidth || 1200; // sane fallback before first measurement
  const gridWidth = zoomScale !== 1 ? baseGridWidth / zoomScale : baseGridWidth;

  // Let grid grow vertically — no maxRows cap so components below others can resize freely

  // Overflow ("⋯") menu — collects secondary actions out of the toolbar.
  const overflowItems = [
    { key: 'clone',   icon: <CopyOutlined />,        label: 'Clone dashboard',     onClick: handleCloneDashboard },
    { key: 'library', icon: <FolderOpenOutlined />,  label: 'Open library',        onClick: () => setLibraryOpen(true) },
    { type: 'divider' },
    {
      key: 'density', icon: <SettingOutlined />, label: 'Density',
      children: [
        { key: 'd-default',     label: density === 'default'     ? '✓ Default'     : 'Default',     onClick: () => setDensity('default') },
        { key: 'd-compact',     label: density === 'compact'     ? '✓ Compact'     : 'Compact',     onClick: () => setDensity('compact') },
        { key: 'd-comfortable', label: density === 'comfortable' ? '✓ Comfortable' : 'Comfortable', onClick: () => setDensity('comfortable') },
      ],
    },
    {
      key: 'zoom', icon: <ZoomInOutlined />, label: `Zoom: ${zoomPercent}%`,
      children: [
        { key: 'z-50',  label: '50%',  onClick: () => setZoomPercent(50) },
        { key: 'z-75',  label: '75%',  onClick: () => setZoomPercent(75) },
        { key: 'z-100', label: '100%', onClick: () => setZoomPercent(100) },
        { key: 'z-125', label: '125%', onClick: () => setZoomPercent(125) },
        { key: 'z-150', label: '150%', onClick: () => setZoomPercent(150) },
      ],
    },
    { type: 'divider' },
    {
      key: 'developer', icon: <ExperimentOutlined />, label: 'Developer',
      // Developer tools call the server `/json` and `/meta` endpoints,
      // which require a persisted dashboard. Disable the entire group
      // while the active dashboard is still an unsaved draft.
      disabled: !!draftDashboard && activeDashboardId === draftDashboard.id,
      children: [
        { key: 'dash-state',    icon: <CodeOutlined />, label: 'View dashboard state as JSON',   onClick: handleShowDashboardState },
        { key: 'dash-metadata', icon: <EditOutlined />, label: 'View & Edit dashboard metadata', onClick: handleShowDashboardMetadata },
      ],
    },
  ];

  return (
    <div className={`dashboard-panel dashboard-panel-open dashboard-density-${density}${isFullscreen ? ' dashboard-panel-fullscreen' : ''}${editEnabled ? '' : ' dashboard-panel-viewer'}`}>
      {/* Panel Header — single row containing tab nav + action toolbar */}
      <div className="dashboard-panel-header">
        <DashboardTabBar
          onGoHome={handleGoHome}
          onShare={handleShareDashboard}
          isHomePage={showHomepage}
          onSwitchTab={handleSelectDashboard}
        />
        {showHomepage ? (
          <div style={{ display: 'flex', gap: 2, alignItems: 'center', flexShrink: 0, paddingRight: 6 }}>
            <Tooltip title={isFullscreen ? 'Exit Fullscreen (Esc)' : 'Fullscreen'}>
              <Button type="text" icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />} onClick={toggleFullscreen} className="dashboard-collapse-btn" />
            </Tooltip>
            {!isFullscreen && (
              <Tooltip title='Collapse Dashboard'>
                <Button type="text" icon={<LeftOutlined />} onClick={closeDashboard} className="dashboard-collapse-btn" />
              </Tooltip>
            )}
          </div>
        ) : (
          <div className="dx-row-tools dx-row-tools-inline">
            {/* ── Add chart (creation) ───────────────────────── */}
            {editEnabled && (
              <Tooltip title="New chart / table / metric">
                <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => openEditor(null)}>
                  Add chart
                </Button>
              </Tooltip>
            )}
            {!editEnabled && !canEditCurrent && (
              <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.85)' }}>
                Read-only
              </span>
            )}

            {/* ── Pending changes commit bar (Edit mode, dirty only) ── */}
            {editEnabled && editDirty && (
              <div className="dx-commit-bar" role="group" aria-label="Pending changes">
                <span className="dx-dirty-dot" aria-hidden="true" />
                <span className="dx-commit-label">
                  {pendingChangeCount === 1 ? '1 unsaved change' : `${pendingChangeCount} unsaved changes`}
                </span>
                <Tooltip title="Discard pending changes">
                  <Button
                    size="small"
                    className="dx-commit-cancel"
                    icon={<CloseOutlined />}
                    onClick={handleCancelLayout}
                    disabled={savingLayout}
                  >
                    Cancel
                  </Button>
                </Tooltip>
                <Tooltip title="Save dashboard layout">
                  <Button
                    size="small"
                    type="primary"
                    className="dx-commit-save"
                    icon={<SaveOutlined />}
                    loading={savingLayout}
                    onClick={handleSaveLayout}
                  >
                    Save
                  </Button>
                </Tooltip>
                <span className="dx-divider" />
              </div>
            )}

            {/* ── Right-aligned action group (icon-only) ───────── */}
            {canEditCurrent && (
              <div className="dx-mode-seg dx-mode-seg-icon" role="tablist" aria-label="View / edit mode">
                <Tooltip title="View mode">
                  <span
                    className={`item${!isEditMode ? ' active' : ''}`}
                    role="tab"
                    aria-selected={!isEditMode}
                    aria-label="View mode"
                    onClick={() => confirmDiscardDraft(() => setViewMode('view'))}
                  >
                    <EyeOutlined />
                  </span>
                </Tooltip>
                <Tooltip title="Edit mode">
                  <span
                    className={`item${isEditMode ? ' active' : ''}`}
                    role="tab"
                    aria-selected={isEditMode}
                    aria-label="Edit mode"
                    onClick={() => setViewMode('edit')}
                  >
                    <EditOutlined />
                  </span>
                </Tooltip>
              </div>
            )}
            <Tooltip title="Share Dashboard">
              <Button
                type="text"
                size="small"
                icon={<ShareAltOutlined />}
                aria-label="Share dashboard"
                className="dashboard-collapse-btn"
                onClick={() => {
                  const active = dashboards.find(d => d.id === activeDashboardId);
                  if (active) handleShareDashboard(active);
                }}
              />
            </Tooltip>
            <Tooltip title={isFullscreen ? 'Exit Fullscreen (Esc)' : 'Fullscreen'}>
              <Button type="text" size="small" icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />} onClick={toggleFullscreen} className="dashboard-collapse-btn" />
            </Tooltip>
            <Dropdown menu={{ items: overflowItems }} trigger={['click']} placement="bottomRight">
              <Tooltip title="More">
                <Button type="text" size="small" icon={<MoreOutlined />} className="dashboard-collapse-btn" />
              </Tooltip>
            </Dropdown>
            {!isFullscreen && (
              <Tooltip title='Collapse Dashboard'>
                <Button type="text" size="small" icon={<LeftOutlined />} onClick={closeDashboard} className="dashboard-collapse-btn" />
              </Tooltip>
            )}
          </div>
        )}
      </div>

      {/* Dashboard-level Filters (only when viewing a dashboard) */}
      {!showHomepage && <DashboardFilterBar />}

      {!showHomepage && (
        <div style={{ padding: '8px 12px 0 12px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Tabs
              className="dashboard-content-tabs"
              size="small"
              activeKey={activeContentTab}
              onChange={setActiveContentTab}
              type={editEnabled ? 'editable-card' : 'card'}
              hideAdd={!editEnabled}
              onEdit={(targetKey, action) => {
                if (action === 'add') {
                  setCreateTabModalOpen(true);
                  return;
                }
                if (action === 'remove') {
                  handleDeleteDashboardTab(String(targetKey || ''));
                }
              }}
              items={dashboardTabs.map((tab) => {
                const activeDash = dashboards.find((d) => d.id === activeDashboardId);
                const isDefaultTab = activeDash?.default_tab === tab;
                const tabContextItems = [
                  {
                    key: 'rename',
                    icon: <EditOutlined />,
                    label: 'Rename tab',
                    onClick: () => {
                      setRenameTabOldName(tab);
                      setRenameTabNewName(tab);
                      setRenameTabModalOpen(true);
                    },
                  },
                  {
                    key: 'default',
                    icon: isDefaultTab ? <StarFilled style={{ color: '#faad14' }} /> : <StarOutlined />,
                    label: isDefaultTab ? 'Default tab ✓' : 'Set as default tab',
                    onClick: () => handleSetDefaultTab(tab),
                  },
                ];
                return {
                  key: tab,
                  label: (
                    <Dropdown trigger={['contextMenu']} menu={{ items: tabContextItems }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        {isDefaultTab && <StarFilled style={{ fontSize: 10, color: '#faad14' }} />}
                        {tab}
                      </span>
                    </Dropdown>
                  ),
                  closable: editEnabled && dashboardTabs.length > 1,
                };
              })}
              style={{ flex: 1, marginBottom: 0 }}
            />
          </div>
        </div>
      )}

      {/* Panel Content */}
      <div className="dashboard-panel-content" ref={contentRef}>
        {showHomepage ? (
          <DashboardHomepage
            onSelectDashboard={handleSelectDashboard}
            onDashboardDeleted={handleDashboardDeleted}
            onShareDashboard={handleShareDashboard}
            onCreateDashboard={handleCreateDashboard}
          />
        ) : (loading || urlResolving) ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12, padding: 16 }}>
            <div style={{ height: 220 }}><Skeleton kind="chart" /></div>
            <div style={{ height: 220 }}><Skeleton kind="table" /></div>
            <div style={{ height: 220 }}><Skeleton kind="chart" /></div>
          </div>
        ) : items.length === 0 ? (
          <EmptyLaunchpad
            onPick={(type) => openEditor(null, { initialItemType: type })}
          />
        ) : visibleItemsByTab.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={`No components in tab "${activeContentTab}"`}
            style={{ paddingTop: 32 }}
          />
        ) : (
          <div style={zoomScale !== 1 ? { transform: `scale(${zoomScale})`, transformOrigin: 'top left', width: `${100 / zoomScale}%` } : {}}>
            <Responsive
              className="dashboard-grid"
              width={gridWidth}
              layouts={{ lg: layout, md: layout, sm: layout }}
              breakpoints={{ lg: 996, md: 768, sm: 480 }}
              cols={{ lg: numCols, md: numCols, sm: 4 }}
              rowHeight={60}
              draggableHandle=".drag-handle"
              draggableCancel=".no-drag"
              onDragStart={handleDragOrResizeStart}
              onDragStop={handleDragOrResizeStop}
              onResizeStart={handleDragOrResizeStart}
              onResizeStop={handleDragOrResizeStop}
              // Horizontal compaction in both modes: when an item moves,
              // the rest slide left to fill the gap (so dragging Component B
              // out from <A B C> leaves <A C> on the original row, not
              // <A _ C>).
              compactType="horizontal"
              preventCollision={false}
              isDraggable={editEnabled}
              isResizable={editEnabled}
              margin={[GRID_GAP, GRID_GAP]}
            >
              {visibleItemsByTab.map((item) => (
                <div key={String(item.id)}>
                  <ObservedCard
                    item={item}
                    onRemove={handleRemoveItem}
                    isFocused={focusedComponent && focusedComponent.id === item.id}
                    onFocus={focusComponent}
                    onEditItem={openEditor}
                    onMoveToTab={handleMoveItemToTab}
                    availableTabs={dashboardTabs}
                    canEdit={editEnabled}
                    isInteracting={isInteracting}
                  />
                </div>
              ))}
            </Responsive>
          </div>
        )}
      </div>

      <Modal
        title="Rename Tab"
        open={renameTabModalOpen}
        onCancel={() => { setRenameTabModalOpen(false); setRenameTabOldName(''); setRenameTabNewName(''); }}
        onOk={handleRenameTab}
        okText="Save"
        width={360}
      >
        <Input
          value={renameTabNewName}
          onChange={(e) => setRenameTabNewName(e.target.value)}
          onPressEnter={handleRenameTab}
          maxLength={50}
          placeholder="Tab name"
          autoFocus
        />
      </Modal>

      <Modal
        title="Create New Tab"
        open={createTabModalOpen}
        onCancel={() => { setCreateTabModalOpen(false); setNewTabName(''); }}
        onOk={handleCreateDashboardTab}
        okText="Create"
        width={420}
      >
        <Input
          value={newTabName}
          onChange={(e) => setNewTabName(e.target.value)}
          onPressEnter={handleCreateDashboardTab}
          maxLength={64}
          placeholder="Tab name"
          autoFocus
        />
      </Modal>

      {/* Dashboard Library Modal */}
      <DashboardLibrary
        open={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        onSelectDashboard={handleSelectDashboard}
        onShareDashboard={handleShareDashboard}
      />

      {/* Share Dialog */}
      <DashboardShareDialog
        open={shareDialog.visible}
        onClose={() => setShareDialog({ visible: false, dashboard: null })}
        dashboard={shareDialog.dashboard}
      />

      {/* Dashboard state as JSON — read-only snapshot (metadata + data) */}
      <Modal
        title="📋 Dashboard state as JSON"
        open={dashStateModalOpen}
        onCancel={() => setDashStateModalOpen(false)}
        width={900}
        footer={[
          <Button
            key="copy"
            disabled={dashStateLoading || !dashStateString}
            onClick={() => {
              navigator.clipboard.writeText(dashStateString);
              setDashStateCopied(true);
              setTimeout(() => setDashStateCopied(false), 2000);
            }}
          >{dashStateCopied ? '✓ Copied!' : 'Copy to Clipboard'}</Button>,
          <Button key="close" type="primary" onClick={() => setDashStateModalOpen(false)}>Close</Button>,
        ]}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 8 }}
          message="Read-only snapshot. Includes each item's SQL, chart config, layout, and the rows currently returned by its query — paste into an agent to reason about this dashboard."
        />
        {dashStateLoading ? (
          <div style={{ padding: 24, textAlign: 'center' }}>
            <Spin /> <span style={{ marginLeft: 8 }}>Executing item queries…</span>
          </div>
        ) : (
          <pre style={{
            backgroundColor: '#f5f5f5', padding: 16, borderRadius: 4,
            maxHeight: 600, overflow: 'auto',
            fontFamily: 'Consolas, Monaco, "Courier New", monospace',
            fontSize: 13, lineHeight: 1.5,
          }}>
            {dashStateString}
          </pre>
        )}
      </Modal>

      {/* Dashboard metadata — View / Edit / Copy / Clone (full export payload) */}
      <Modal
        title={dashMetaEditing ? '✏️ Edit dashboard metadata' : '🛠️ Dashboard metadata'}
        open={dashMetaModalOpen}
        onCancel={() => { setDashMetaModalOpen(false); setDashMetaEditing(false); setDashMetaError(''); }}
        width={900}
        footer={dashMetaEditing ? [
          <Button key="cancel" onClick={() => {
            setDashMetaString(dashMetaOriginal);
            setDashMetaEditing(false);
            setDashMetaError('');
          }}>Cancel</Button>,
          <Button key="save" type="primary" loading={dashMetaSaving} onClick={handleSaveDashboardMetadata}>Save</Button>,
        ] : [
          <Button key="copy" onClick={() => {
            navigator.clipboard.writeText(dashMetaString);
            setDashMetaCopied(true);
            setTimeout(() => setDashMetaCopied(false), 2000);
          }}>{dashMetaCopied ? '✓ Copied!' : 'Copy to Clipboard'}</Button>,
          <Button key="edit" type="primary" icon={<EditOutlined />} disabled={!canEditCurrent} onClick={() => setDashMetaEditing(true)}>Edit</Button>,
          <Button key="close" onClick={() => setDashMetaModalOpen(false)}>Close</Button>,
        ]}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 8 }}
          message="Full editable definition (name, description, items with SQL, chart config, and grid layout). Copy to export this dashboard, paste into another environment's metadata editor, and Save to import."
        />
        {dashMetaError && <Alert type="error" message={dashMetaError} style={{ marginBottom: 8 }} showIcon />}
        {!canEditCurrent && !dashMetaEditing && (
          <Alert
            type="warning"
            message="You have read-only access to this dashboard. Use Clone to make an editable copy."
            style={{ marginBottom: 8 }}
            showIcon
          />
        )}
        {dashMetaEditing ? (
          <Input.TextArea
            value={dashMetaString}
            onChange={(e) => setDashMetaString(e.target.value)}
            rows={22}
            style={{ fontFamily: 'Consolas, Monaco, "Courier New", monospace', fontSize: 13 }}
          />
        ) : (
          <pre style={{
            backgroundColor: '#f5f5f5', padding: 16, borderRadius: 4,
            maxHeight: 560, overflow: 'auto',
            fontFamily: 'Consolas, Monaco, "Courier New", monospace',
            fontSize: 13, lineHeight: 1.5,
          }}>
            {dashMetaString}
          </pre>
        )}
      </Modal>

      {/* Slide-in Plotly chart editor (overlays the panel content) */}
      <ChartEditorPanel
        open={editorOpen}
        onClose={closeEditor}
        onSaved={closeEditor}
        existingItem={editingItem}
        initialItemType={editorInitialType}
      />
    </div>
  );
}
